import axios from 'axios';
import { SHEET_IDS } from '../core/config';
const MASTER_SHEET_ID = SHEET_IDS.MASTER;
// ============================================================
//  HELPER: Generate User ID
// ============================================================

const generateUserId = (name, surname) => {
  const now = Date.now();
  const initials = (name?.charAt(0) || 'X') + (surname?.charAt(0) || 'X');
  const random = Math.random().toString(36).substring(2, 5);
  return `U${now}_${initials.toUpperCase()}_${random}`;
};



// ============================================================
//  USER DIRECTORY DATA FUNCTIONS
// ============================================================

/**
 * Fetch all entries from the UserDirectory sheet.
 * Columns: Email, OrganisationID, UserID, Name, Surname, Role
 */
export const fetchAllUserDirectory = async (token) => {  // 👈 removed spreadsheetId
  try {
    const range = 'UserDirectory!A2:F';
    const response = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/${range}`,  // 👈 hard-coded master
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.data.values) return [];
    return response.data.values.map((row, index) => {
      const [email, organisationId, userId, name, surname, role] = row;
      return {
        email: email || '',
        organisationId: organisationId || '',
        userId: userId || '',
        name: name || '',
        surname: surname || '',
        role: role || '',
        rowNumber: index + 2,
      };
    });
  } catch (err) {
    console.error('Error fetching UserDirectory:', err);
    return [];
  }
};





// ============================================================
//  ORGANISATION MANAGEMENT (Super Admin only)
// ============================================================

/**
 * Fetch all organisations from the master spreadsheet.
 */
export const fetchAllOrganisations = async (token) => {
  try {
    const range = 'Organisations!A2:I';
    const response = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/${range}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const rows = response.data.values || [];
    return rows.map((row, index) => ({
      organisationId: row[0] || '',
      organisationName: row[1] || '',
      organisationCode: row[2] || '',
      spreadsheetId: row[3] || '',
      status: row[4] || '',
      usersSpreadsheetId: row[5] || '',
      attendanceSpreadsheetId: row[6] || '',
      marksSpreadsheetId: row[7] || '',
      topicsSpreadsheetId: row[8] || '',
      rowNumber: index + 2,
    }));
  } catch (err) {
    console.error('Error fetching organisations:', err);
    return [];
  }
};

/**
 * Add a new organisation.
 */
export const addOrganisation = async (token, orgData) => {
  try {
    const range = 'Organisations!A:I';
    const row = [
      orgData.organisationId || '',
      orgData.organisationName || '',
      orgData.organisationCode || '',
      orgData.spreadsheetId || '',
      orgData.status || 'active',
      orgData.usersSpreadsheetId || '',
      orgData.attendanceSpreadsheetId || '',
      orgData.marksSpreadsheetId || '',
      orgData.topicsSpreadsheetId || '',
    ];
    await axios.post(
      `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/${range}:append`,
      { values: [row] },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  } catch (err) {
    console.error('Error adding organisation:', err);
    throw err;
  }
};

/**
 * Update an existing organisation.
 */
export const updateOrganisation = async (token, rowNumber, orgData) => {
  try {
    const range = `Organisations!A${rowNumber}:I${rowNumber}`;
    const row = [
      orgData.organisationId || '',
      orgData.organisationName || '',
      orgData.organisationCode || '',
      orgData.spreadsheetId || '',
      orgData.status || 'active',
      orgData.usersSpreadsheetId || '',
      orgData.attendanceSpreadsheetId || '',
      orgData.marksSpreadsheetId || '',
      orgData.topicsSpreadsheetId || '',
    ];
    await axios.put(
      `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/${range}`,
      { values: [row] },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  } catch (err) {
    console.error('Error updating organisation:', err);
    throw err;
  }
};


/**
 * Delete an organisation (physically removes the row).
 * Matches the adminDeleteUser behaviour.
 */
/*export const deleteOrganisation = async (token, rowNumber) => {
  try {
    const sheetId = await getSheetIdByName(token, MASTER_SHEET_ID, 'Organisations');
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}:batchUpdate`;
    await axios.post(
      url,
      {
        requests: [
          {
            deleteDimension: {
              range: {
                sheetId: sheetId,
                dimension: 'ROWS',
                startIndex: rowNumber - 1, // 0‑based
                endIndex: rowNumber,       // exclusive
              },
            },
          },
        ],
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  } catch (err) {
    console.error('Error deleting organisation:', err);
    throw err;
  }
};*/


export const deleteOrganisation = async (token, rowNumber) => {
  try {
    // 1. Get organisationId before deleting
    const allOrgs = await fetchAllOrganisations(token);
    const org = allOrgs.find(o => o.rowNumber === rowNumber);
    if (!org) throw new Error(`Organisation with rowNumber ${rowNumber} not found.`);

    // 2. Delete all UserDirectory rows for this organisation
    const userDirectory = await fetchAllUserDirectory(token);
    const userDirRows = userDirectory.filter(entry => entry.organisationId === org.organisationId);
    for (const entry of userDirRows) {
      await deleteRowFromSheet(token, MASTER_SHEET_ID, 'UserDirectory', entry.rowNumber);
    }

    // 3. Delete the organisation row itself
    await deleteRowFromSheet(token, MASTER_SHEET_ID, 'Organisations', rowNumber);

  } catch (err) {
    console.error('Error deleting organisation:', err);
    throw err;
  }
};

// ============================================================
//  DATA ACCESS FUNCTIONS
// ============================================================

export const fetchUsersWithClasses = async (token, spreadsheetId) => {
  try {
    const ranges = [
      'Users!A2:S',   // 19 columns
      'Classes!A2:I'  // legacy
    ];
    const params = new URLSearchParams();
    ranges.forEach(range => params.append('ranges', range));
    params.append('majorDimension', 'ROWS');

    const response = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values:batchGet?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const valueRanges = response.data.valueRanges;
    const userRows = valueRanges[0]?.values || [];
    const classRows = valueRanges[1]?.values || [];

    // Build class objects by tutor email (legacy)
    const classesByEmail = {};
    classRows.forEach(row => {
      const [email, googleClassroomLink, subject, day, startTime, duration, zoomLink, zoomPassword, grade] = row;
      if (!email) return;
      const classObj = {
        googleClassroomLink: googleClassroomLink || '',
        subject: subject || '',
        day: day || '',
        startTime: startTime || '',
        duration: duration ? parseInt(duration, 10) : 0,
        zoomLink: zoomLink || '',
        zoomPassword: zoomPassword || '',
        grade: grade || ''
      };
      if (!classesByEmail[email]) classesByEmail[email] = [];
      classesByEmail[email].push(classObj);
    });

    // Build user objects
    const users = userRows.map((row, index) => {
      const [
        userId, organisationId,
        name, surname, email, role, grade,
        school, province, district, circuit,
        teacher, subject,
        googleClassroomLink, schedule, zoomLink, zoomPassword,
        studentType, // 👈 column R
        // column S reserved
      ] = row;
      if (!email) return null;

      const parsedSchedule = parseSchedule(schedule);
      const taughtClasses = classesByEmail[email] || [];

      return {
        userId: userId || `R${index + 2}`,
        organisationId: organisationId || '',
        name: name || '',
        surname: surname || '',
        email: email || '',
        role: role || '',
        grade: grade || '',
        school: school || '',
        province: province || '',
        district: district || '',
        circuit: circuit || '',
        teacher: teacher || '',
        subject: subject || '',
        googleClassroomLink: googleClassroomLink || '',
        schedule: parsedSchedule,
        day: parsedSchedule.length > 0 ? parsedSchedule[0].day : '',
        startTime: parsedSchedule.length > 0 ? parsedSchedule[0].startTime : '',
        duration: parsedSchedule.length > 0 ? parsedSchedule[0].duration : 0,
        zoomLink: zoomLink || '',
        zoomPassword: zoomPassword || '',
        studentType: studentType || 'group', // 👈 added
        classes: taughtClasses,
        rowNumber: index + 2
      };
    }).filter(Boolean);

    return users;
  } catch (err) {
    console.error('Error fetching users with classes:', err);
    return [];
  }
};






// ============================================================
//  SCHEDULE + TERM DATA HELPERS
// ============================================================

const TERM_START_MARKER = 'TERM_START';
const TERM_END_MARKER = 'TERM_END';

/**
 * Parse schedule string into { schedule, termData }.
 * - Term entries are recognised by day === 'TERM_START' or 'TERM_END'.
 * - All other entries are treated as normal schedule sessions.
 * @param {string} str - Serialised schedule string
 * @returns {{ schedule: Array, termData: Array }}
 */
export const parseScheduleWithTerms = (str) => {
  if (!str || str.trim() === '') {
    return { schedule: [], termData: [] };
  }

  const parts = str.split(';').filter(p => p.trim() !== '');
  const schedule = [];
  const termData = [];

  parts.forEach(part => {
    const [day, startTime, duration] = part.split('|');
    if (day === TERM_START_MARKER) {
      termData.push({ term: startTime || 'Term 1', termStart: duration || '', termEnd: '' });
    } else if (day === TERM_END_MARKER) {
      if (termData.length > 0) {
        termData[termData.length - 1].termEnd = startTime || '';
      } else {
        termData.push({ term: 'Term 1', termStart: '', termEnd: startTime || '' });
      }
    } else {
      schedule.push({
        day: day || '',
        startTime: startTime || '',
        duration: parseInt(duration, 10) || 0,
      });
    }
  });

  return { schedule, termData };
};
// ============================================================
//  SCHEDULE HELPER FUNCTIONS
// ============================================================

/**
 * Serialize an array of sessions to a string.
 * @param {Array} sessions - Array of { day, startTime, duration }
 * @returns {string} - e.g., "Monday|09:00|60;Wednesday|14:00|90"
 */
/*export const serializeSchedule = (sessions) => {
  if (!sessions || sessions.length === 0) return '';
  return sessions
    .map(s => `${s.day}|${s.startTime}|${s.duration}`)
    .join(';');
};*/


/**
 * Serialize schedule and term data into a single string.
 * Term entries are placed first, then regular sessions.
 * @param {Array} schedule - Array of { day, startTime, duration }
 * @param {Array} termData - Array of { term, termStart, termEnd }
 * @returns {string} Serialised string
 */
export const serializeScheduleWithTerms = (schedule, termData) => {
  const parts = [];

  (termData || []).forEach(t => {
    if (t.termStart) {
      parts.push(`${TERM_START_MARKER}|${t.term}|${t.termStart}`);
    }
    if (t.termEnd) {
      parts.push(`${TERM_END_MARKER}|${t.termEnd}|`);
    }
  });

  (schedule || []).forEach(s => {
    parts.push(`${s.day}|${s.startTime}|${s.duration}`);
  });

  return parts.join(';');
};


export const serializeSchedule = (schedule) => {
  return serializeScheduleWithTerms(schedule, []);
};

/**
 * Parse a schedule string back to an array of sessions.
 * @param {string} str - Schedule string
 * @returns {Array} - Array of { day, startTime, duration }
 */
/*export const parseSchedule = (str) => {
  if (!str || str.trim() === '') return [];
  return str.split(';')
    .filter(part => part.trim() !== '')
    .map(part => {
      const [day, startTime, duration] = part.split('|');
      return {
        day: day || '',
        startTime: startTime || '',
        duration: parseInt(duration, 10) || 0,
      };
    });
};*/

export const parseSchedule = (str) => {
  const { schedule } = parseScheduleWithTerms(str);
  return schedule;
};



// ============================================================
//  DATA ACCESS FUNCTIONS
// ============================================================

// src/services/sheetDataService.js

/*export const fetchAllUsers = async (token, spreadsheetId) => {
  try {
    const range = 'Users!A2:T'; // now 20 columns (A through T) – Status is column T
    const response = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.data.values) return [];
    return response.data.values.map((row, index) => {
      const [
        userId, organisationId,
        name, surname, email, role, grade,
        school, province, district, circuit,
        teacher, subject,
        googleClassroomLink, schedule, zoomLink, zoomPassword,
        studentType, gender, status, // 👈 column T = Status
      ] = row;
      return {
        userId: userId || `R${index + 2}`,
        organisationId: organisationId || '',
        name: name || '',
        surname: surname || '',
        email: email || '',
        role: role || '',
        grade: grade || '',
        school: school || '',
        province: province || '',
        district: district || '',
        circuit: circuit || '',
        teacher: teacher || '',
        subject: subject || '',
        googleClassroomLink: googleClassroomLink || '',
        schedule: parseSchedule(schedule),
        zoomLink: zoomLink || '',
        zoomPassword: zoomPassword || '',
        studentType: studentType || 'group',
        gender: gender || '',
        status: status || 'active', // 👈 default to active
        rowNumber: index + 2
      };
    });
  } catch (err) {
    console.error('Error fetching all users:', err);
    return [];
  }
};*/


export const fetchAllUsers = async (token, spreadsheetId) => {
  try {
    const range = 'Users!A2:T';
    const response = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.data.values) return [];
    return response.data.values.map((row, index) => {
      const [
        userId, organisationId,
        name, surname, email, role, grade,
        school, province, district, circuit,
        teacher, subject,
        googleClassroomLink, scheduleStr, zoomLink, zoomPassword,
        studentType, gender, status,
      ] = row;

      // ✅ CHANGED: Use parseScheduleWithTerms to get both schedule and termData
      const { schedule, termData } = parseScheduleWithTerms(scheduleStr || '');

      return {
        userId: userId || `R${index + 2}`,
        organisationId: organisationId || '',
        name: name || '',
        surname: surname || '',
        email: email || '',
        role: role || '',
        grade: grade || '',
        school: school || '',
        province: province || '',
        district: district || '',
        circuit: circuit || '',
        teacher: teacher || '',
        subject: subject || '',
        googleClassroomLink: googleClassroomLink || '',
        schedule: schedule,
        termData: termData,          // ✅ NEW: term data from schedule
        zoomLink: zoomLink || '',
        zoomPassword: zoomPassword || '',
        studentType: studentType || 'group',
        gender: gender || '',
        status: status || 'active',
        rowNumber: index + 2
      };
    });
  } catch (err) {
    console.error('Error fetching all users:', err);
    return [];
  }
};

export const fetchAllClasses = async (token, spreadsheetId) => {
  try {
    const range = 'Classes!A2:I';
    const response = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.data.values) return [];
    return response.data.values.map((row, index) => {
      const [email, googleClassroomLink, subject, day, startTime, duration, zoomLink, zoomPassword, grade] = row;
      return {
        email: email || '',
        googleClassroomLink: googleClassroomLink || '',
        subject: subject || '',
        day: day || '',
        startTime: startTime || '',
        duration: duration ? parseInt(duration, 10) : 0,
        zoomLink: zoomLink || '',
        zoomPassword: zoomPassword || '',
        grade: grade || '',
        rowNumber: index + 2
      };
    });
  } catch (err) {
    console.error('Error fetching all classes:', err);
    return [];
  }
};

export const fetchUserClasses = async (token, spreadsheetId, userId) => {
  // This function is kept for compatibility but may not be used.
  // It still reads from Enrollments and Classes sheets.
  try {
    const enrollRange = 'Enrollments!A2:B';
    const enrollResponse = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${enrollRange}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const enrollRows = enrollResponse.data.values || [];
    const classIds = enrollRows
      .filter(row => row[0] === userId)
      .map(row => row[1]);
    if (classIds.length === 0) return [];

    const classRange = 'Classes!A2:G';
    const classResponse = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${classRange}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const classRows = classResponse.data.values || [];
    return classRows
      .filter(row => classIds.includes(row[0]))
      .map(row => ({
        classId: row[0],
        subject: row[1] || '',
        day: row[2] || '',
        startTime: row[3] || '',
        duration: parseInt(row[4], 10) || 0,
        zoomLink: row[5] || '',
        googleClassroomLink: row[6] || '',
      }));
  } catch (error) {
    console.error('Error fetching user classes:', error);
    return [];
  }
};

/*export const appendUser = async (token, spreadsheetId, userData) => {
  try {
    const range = 'Users!A:T';
    const userId = userData.userId || generateUserId(userData.name, userData.surname);

    let schedule = userData.schedule || [];
    if (schedule.length === 0 && (userData.day || userData.startTime || userData.duration)) {
      schedule = [{
        day: userData.day || '',
        startTime: userData.startTime || '',
        duration: parseInt(userData.duration, 10) || 0
      }];
    }
    const scheduleStr = serializeSchedule(schedule);
    const values = [[
      userId,
      userData.organisationId || 'N/A',
      userData.name,
      userData.surname,
      userData.email,
      userData.role || 'Student',
      userData.grade || 'N/A',
      userData.school || 'N/A',
      userData.province || 'N/A',
      userData.district || 'N/A',
      userData.circuit || 'N/A',
      userData.teacher || 'N/A',
      userData.subject || 'N/A',
      userData.googleClassroomLink || 'N/A',
      scheduleStr,
      userData.zoomLink || 'N/A',
      userData.zoomPassword || 'N/A',
      userData.studentType || 'group',
      userData.gender || '',
      userData.status || 'active', // 👈 Status column
    ]];

    const response = await axios.post(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}:append`,
      { values },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error appending user:', error);
    throw error;
  }
};*/

export const appendUser = async (token, spreadsheetId, userData) => {
  try {
    const range = 'Users!A:T';
    const userId = userData.userId || generateUserId(userData.name, userData.surname);

    // ✅ CHANGED: Get both schedule and termData
    const schedule = userData.schedule || [];
    const termData = userData.termData || [];
    const scheduleStr = serializeScheduleWithTerms(schedule, termData);

    const values = [[
      userId,
      userData.organisationId || 'N/A',
      userData.name,
      userData.surname,
      userData.email,
      userData.role || 'Student',
      userData.grade || 'N/A',
      userData.school || 'N/A',
      userData.province || 'N/A',
      userData.district || 'N/A',
      userData.circuit || 'N/A',
      userData.teacher || 'N/A',
      userData.subject || 'N/A',
      userData.googleClassroomLink || 'N/A',
      scheduleStr,
      userData.zoomLink || 'N/A',
      userData.zoomPassword || 'N/A',
      userData.studentType || 'group',
      userData.gender || '',
      userData.status || 'active',
    ]];

    const response = await axios.post(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}:append`,
      { values },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error appending user:', error);
    throw error;
  }
};

export const updateUserDirectoryByRow = async (token, rowNumber, userDirData) => {  // 👈 removed spreadsheetId
  try {
    const range = `UserDirectory!A${rowNumber}:F${rowNumber}`;
    const values = [[
      userDirData.email || '',
      userDirData.organisationId || '',
      userDirData.userId || '',
      userDirData.name || '',
      userDirData.surname || '',
      userDirData.role || 'Student'
    ]];
    const response = await axios.put(
      `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/${range}`,  // 👈 hard-coded master
      { values },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating UserDirectory:', error);
    throw error;
  }
};

/*export const updateUserByRow = async (token, spreadsheetId, rowNumber, userData) => {
  try {
    const range = `Users!A${rowNumber}:T${rowNumber}`;
    let schedule = userData.schedule || [];
    if (schedule.length === 0 && (userData.day || userData.startTime || userData.duration)) {
      schedule = [{
        day: userData.day || '',
        startTime: userData.startTime || '',
        duration: parseInt(userData.duration, 10) || 0
      }];
    }
    const scheduleStr = serializeSchedule(schedule);
    const values = [[
      userData.userId || '',
      userData.organisationId || 'N/A',
      userData.name,
      userData.surname,
      userData.email,
      userData.role || 'Student',
      userData.grade || 'N/A',
      userData.school || 'N/A',
      userData.province || 'N/A',
      userData.district || 'N/A',
      userData.circuit || 'N/A',
      userData.teacher || 'N/A',
      userData.subject || 'N/A',
      userData.googleClassroomLink || 'N/A',
      scheduleStr,
      userData.zoomLink || 'N/A',
      userData.zoomPassword || 'N/A',
      userData.studentType || 'group',
      userData.gender || '',
      userData.status || 'active', // 👈 Status column
    ]];

    const response = await axios.put(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { values },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};*/


export const updateUserByRow = async (token, spreadsheetId, rowNumber, userData) => {
  try {
    const range = `Users!A${rowNumber}:T${rowNumber}`;

    // ✅ CHANGED: Get both schedule and termData
    const schedule = userData.schedule || [];
    const termData = userData.termData || [];
    const scheduleStr = serializeScheduleWithTerms(schedule, termData);

    const values = [[
      userData.userId || '',
      userData.organisationId || 'N/A',
      userData.name,
      userData.surname,
      userData.email,
      userData.role || 'Student',
      userData.grade || 'N/A',
      userData.school || 'N/A',
      userData.province || 'N/A',
      userData.district || 'N/A',
      userData.circuit || 'N/A',
      userData.teacher || 'N/A',
      userData.subject || 'N/A',
      userData.googleClassroomLink || 'N/A',
      scheduleStr,  // ✅ This now includes term markers if present
      userData.zoomLink || 'N/A',
      userData.zoomPassword || 'N/A',
      userData.studentType || 'group',
      userData.gender || '',
      userData.status || 'active',
    ]];

    const response = await axios.put(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { values },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const clearUserRow = async (token, spreadsheetId, rowNumber) => {
  try {
    const range = `Users!A${rowNumber}:S${rowNumber}`;
    const emptyRow = [Array(19).fill('')];
    const response = await axios.put(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { values: emptyRow },
      {
        params: { valueInputOption: 'RAW' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error clearing user row:', error);
    throw error;
  }
};

export const appendClass = async (token, spreadsheetId, classData) => {
  try {
    const range = 'Classes!A:I';
    const values = [[
      classData.email,
      classData.googleClassroomLink || '',
      classData.subject,
      classData.day,
      classData.startTime,
      classData.duration,
      classData.zoomLink || '',
      classData.zoomPassword || '',
      classData.grade || ''
    ]];
    const response = await axios.post(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}:append`,
      { values },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error appending class:', error);
    throw error;
  }
};

export const updateClassByRow = async (token, spreadsheetId, rowNumber, classData) => {
  try {
    const range = `Classes!A${rowNumber}:I${rowNumber}`;
    const values = [[
      classData.email,
      classData.googleClassroomLink || '',
      classData.subject,
      classData.day,
      classData.startTime,
      classData.duration,
      classData.zoomLink || '',
      classData.zoomPassword || '',
      classData.grade || ''
    ]];
    const response = await axios.put(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { values },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating class:', error);
    throw error;
  }
};

export const clearClassRow = async (token, spreadsheetId, rowNumber) => {
  try {
    const range = `Classes!A${rowNumber}:I${rowNumber}`;
    const emptyRow = [['', '', '', '', '', '', '', '', '']];
    const response = await axios.put(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { values: emptyRow },
      {
        params: { valueInputOption: 'RAW' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error clearing class row:', error);
    throw error;
  }
};

// ============================================================
//  ATTENDANCE FUNCTIONS
// ============================================================

/**
 * Fetch all attendance records for a class (teacher + subject + grade).
 */
/**
 * Fetch all attendance records for a class.
 */

export const fetchAttendanceForClass = async (token, spreadsheetId, teacher, subject, grade) => {
  try {
    const range = 'Attendance!A2:R'; // now includes R
    const response = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const rows = response.data.values || [];
    return rows
      .filter(row =>
        row[9] === teacher &&
        row[10] === subject &&
        row[11] === grade
      )
      .map(row => ({
        userId: row[0],
        organisationId: row[1],
        name: row[2],
        surname: row[3],
        email: row[4],
        school: row[5],
        province: row[6],
        district: row[7],
        circuit: row[8],
        teacher: row[9],
        subject: row[10],
        grade: row[11],
        term: row[12] || '',
        date: row[13],
        attendance: row[14] || 'Present',
        lastModified: row[15] || null,
        activity: row[16] || '',
        gender: row[17] || '',
      }));
  } catch (error) {
    console.error('Error fetching attendance:', error);
    return [];
  }
};

/**
 * Set (upsert) a single attendance record.
 * Uses composite key: (userId, teacher, subject, grade, date) to avoid duplicates.
 */


export const setAttendance = async (token, spreadsheetId, attendanceData) => {
  const { userId, organisationId, name, surname, email, school, province, district, circuit,
    teacher, subject, grade, term, date, attendance, lastModified, activity, gender } = attendanceData;

  const now = lastModified || new Date().toISOString();
  const newRow = [
    userId || '',
    organisationId || '',
    name || '',
    surname || '',
    email || '',
    school || '',
    province || '',
    district || '',
    circuit || '',
    teacher || '',
    subject || '',
    grade || '',
    term || '',
    date || '',
    attendance || 'Present',
    now,
    activity || '',
    gender || '',
  ];

  const range = 'Attendance!A2:R';
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const rows = response.data.values || [];
  const existingIndex = rows.findIndex(row =>
    row[0] === userId &&
    row[9] === teacher &&
    row[10] === subject &&
    row[11] === grade &&
    row[13] === date
  );
  const rowNumber = existingIndex + 2;

  if (existingIndex !== -1) {
    const updateRange = `Attendance!A${rowNumber}:R${rowNumber}`;
    await axios.put(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${updateRange}`,
      { values: [newRow] },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  } else {
    const appendRange = 'Attendance!A:R';
    await axios.post(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${appendRange}:append`,
      { values: [newRow] },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  }
};

// ============================================================
//  STUDENT MANAGEMENT FUNCTIONS
// ============================================================

/**
 * Fetch students enrolled in a specific class.
 * Since we no longer have classCodes, we filter by teacher email, subject, and grade.
 * The classItem should have email (teacher), subject, and grade.
 */
/*export const fetchClassStudents = async (token, spreadsheetId, classItem) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);

  const teacherEmail = (classItem.email || '').trim().toLowerCase();
  const subject = (classItem.subject || '').trim().toLowerCase();
  const grade = (classItem.grade || '').trim().toLowerCase();

  // Get UserDirectory mapping
  const userDirRange = 'UserDirectory!A:F';
  const userDirResponse = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/${userDirRange}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const userDirRows = userDirResponse.data.values || [];
  const userDirMap = new Map();
  userDirRows.forEach((row, index) => {
    const userId = row[2]?.trim();
    if (userId) {
      userDirMap.set(userId, index + 2);
    }
  });

  const filteredStudents = allUsers.filter(user => {
    const role = (user.role || '').toLowerCase();
    const userTeacher = (user.teacher || '').trim().toLowerCase();
    const userSubject = (user.subject || '').trim().toLowerCase();
    const userGrade = (user.grade || '').trim().toLowerCase();
    const isGroup = !user.studentType || user.studentType.toLowerCase() === 'group';

    return role === 'student' &&
      userTeacher === teacherEmail &&
      userSubject === subject &&
      userGrade === grade &&
      isGroup;
  });

  return filteredStudents.map(user => ({
    userId: user.userId,
    name: user.name,
    surname: user.surname,
    email: user.email,
    grade: user.grade,
    gender: user.gender || '',
    rowNumber: user.rowNumber,
    userDirRowNumber: userDirMap.get(user.userId) || null,
  }));
};*/


export const fetchClassStudents = async (token, spreadsheetId, classItem) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);

  const teacherEmail = (classItem.email || '').trim().toLowerCase();
  const subject = (classItem.subject || '').trim().toLowerCase();
  const grade = (classItem.grade || '').trim().toLowerCase();

  console.log('🔍 Filtering with:', { teacherEmail, subject, grade });

  // Get UserDirectory mapping
  const userDirRange = 'UserDirectory!A:F';
  const userDirResponse = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/${userDirRange}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const userDirRows = userDirResponse.data.values || [];
  const userDirMap = new Map();
  userDirRows.forEach((row, index) => {
    const userId = row[2]?.trim();
    if (userId) {
      userDirMap.set(userId, index + 2);
    }
  });

  const filteredStudents = allUsers.filter(user => {
    const role = (user.role || '').toLowerCase();
    const userTeacher = (user.teacher || '').trim().toLowerCase();
    const userSubject = (user.subject || '').trim().toLowerCase();
    const userGrade = (user.grade || '').trim().toLowerCase();
    const isGroup = !user.studentType || user.studentType.toLowerCase() === 'group';

    return role === 'student' &&
      userTeacher === teacherEmail &&
      userSubject === subject &&
      userGrade === grade &&
      isGroup;
  });

  return filteredStudents.map(user => ({
    userId: user.userId,
    name: user.name,
    surname: user.surname,
    email: user.email,
    grade: user.grade,
    gender: user.gender || '',
    studentType: user.studentType || 'group',  // ✅ Added
    termData: user.termData || [],            // ✅ Added
    rowNumber: user.rowNumber,
    userDirRowNumber: userDirMap.get(user.userId) || null,
  }));
};

/**
 * Add a single student – still uses classId for backward compatibility,
 * but we now also need class details. We'll store the class fields on the student.
 */
export const addStudent = async (token, spreadsheetId, name, surname, email, classData) => {
  // Check in UserDirectory first
  const userDirRange = 'UserDirectory!A:F';
  const userDirResponse = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/${userDirRange}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const userDirRows = userDirResponse.data.values || [];
  const existing = userDirRows.find(row => row[0].toLowerCase() === email.toLowerCase());
  let userId;
  if (existing) {
    userId = existing[2];
  } else {
    userId = generateUserId(name, surname);
    // Add to UserDirectory
    const newRow = [[email, classData.organisationId || '', userId, name, surname, 'Student']];
    await axios.post(
      `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/UserDirectory!A:F:append`,
      { values: newRow },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  }

  // Now add to Users (check duplicate in same class)
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const classKey = `${email.toLowerCase()}|${classData.subject}|${classData.grade}`;
  const existingInClass = allUsers.some(u => `${u.email.toLowerCase()}|${u.subject}|${u.grade}` === classKey);
  if (existingInClass) {
    throw new Error('Student already enrolled in this class.');
  }

  const userData = {
    userId,
    organisationId: classData.organisationId || '',
    name,
    surname,
    email,
    role: 'Student',
    school: classData.school || '',
    province: classData.province || '',
    district: classData.district || '',
    circuit: classData.circuit || '',
    teacher: classData.teacher,
    subject: classData.subject,
    grade: classData.grade,
    googleClassroomLink: classData.googleClassroomLink || '',
    day: classData.day || '',
    startTime: classData.startTime || '',
    duration: classData.duration || '',
    zoomLink: classData.zoomLink || '',
    zoomPassword: classData.zoomPassword || '',
  };
  await appendUser(token, spreadsheetId, userData);
};

export const updateStudent = async (token, usersSheetId, rowNumber, name, surname, email, userDirRowNumber, attendanceSheetId, marksSheetId
) => {
  //const allUsers = await fetchAllUsers(token, spreadsheetId);
  const allUsers = await fetchAllUsers(token, usersSheetId);
  const user = allUsers.find(u => u.rowNumber === rowNumber);
  if (!user) throw new Error('User not found in Users sheet.');

  const userId = user.userId;
  const userRows = allUsers.filter(u => u.userId === userId);
  if (userRows.length === 0) {
    throw new Error(`No Users rows found for userId: ${userId}`);
  }

  // 1. Update Users rows
  const updatePromises = userRows.map(async (u) => {
    const userData = {
      userId: u.userId,
      organisationId: u.organisationId,
      name,
      surname,
      email,
      role: u.role,
      grade: u.grade,
      school: u.school,
      province: u.province,
      district: u.district,
      circuit: u.circuit,
      teacher: u.teacher,
      subject: u.subject,
      googleClassroomLink: u.googleClassroomLink,
      schedule: u.schedule,
      day: u.day,
      startTime: u.startTime,
      duration: u.duration,
      zoomLink: u.zoomLink,
      zoomPassword: u.zoomPassword,
      studentType: u.studentType || 'group',
      gender: u.gender || '',
      status: u.status || 'active',
    };
    await updateUserByRow(token, usersSheetId, u.rowNumber, userData);
  });
  await Promise.all(updatePromises);

  // 2. Update UserDirectory
  //const userDirectoryEntries = await fetchAllUserDirectory(token, spreadsheetId);
  const userDirectoryEntries = await fetchAllUserDirectory(token);
  let targetDirRow = null;

  // First, try by userId (most reliable)
  const dirEntryByUserId = userDirectoryEntries.find(e => e.userId === userId);
  if (dirEntryByUserId) {
    targetDirRow = dirEntryByUserId.rowNumber;
  } else {
    // If not found by userId, try by old email (from the user object before update)
    const oldEmail = user.email;
    const dirEntryByEmail = userDirectoryEntries.find(e => e.email.toLowerCase() === oldEmail.toLowerCase());
    if (dirEntryByEmail) {
      targetDirRow = dirEntryByEmail.rowNumber;
    }
    // If we still don't have a row, and userDirRowNumber is provided, use it with validation
    if (!targetDirRow && userDirRowNumber) {
      const entryAtRow = userDirectoryEntries.find(e => e.rowNumber === userDirRowNumber);
      if (entryAtRow && entryAtRow.userId === userId) {
        targetDirRow = userDirRowNumber;
      } else if (entryAtRow && entryAtRow.userId !== userId) {
        // The row exists but belongs to another user, so we shouldn't use it.
        targetDirRow = null;
      }
    }
  }

  if (targetDirRow) {
    const userDirData = {
      email,
      organisationId: user.organisationId || '',
      userId,
      name,
      surname,
      role: user.role || 'Student'
    };
    //await updateUserDirectoryByRow(token, spreadsheetId, targetDirRow, userDirData);
    await updateUserDirectoryByRow(token, targetDirRow, userDirData);
    console.log(`✅ Updated UserDirectory row ${targetDirRow} for userId: ${userId}`);
  } else {
    // If not found, we need to append a new row (this should not happen often, but handle gracefully)
    console.warn(`UserDirectory row not found for userId: ${userId}. Appending new row.`);
    const userDirRange = 'UserDirectory!A:F';
    const newRow = [
      email,
      user.organisationId || '',
      userId,
      name,
      surname,
      'Student'
    ];
    await axios.post(
      `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/${userDirRange}:append`,
      { values: [newRow] },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    console.log(`✅ Appended new UserDirectory row for userId: ${userId}`);
  }

  // 3. Update Attendance rows
  await updateAttendanceStudentDetails(token, attendanceSheetId, userId, name, surname, email);

  // 4. Update Marks rows
  await updateMarksStudentDetails(token, marksSheetId, userId, name, surname);

  console.log(`✅ Updated ${userRows.length} Users row(s) for userId: ${userId}`);
};

/**
 * Bulk add multiple students.
 * Metadata must include all class fields as well as org details.
 */
/*export const bulkAddStudents = async (token, spreadsheetId, students, metadata, ignoreNameDuplicates = false) => {
  const { organisationId, school, province, district, circuit,
    teacher, subject, grade,
    googleClassroomLink, schedule, zoomLink, zoomPassword,
    studentType
  } = metadata;

  const scheduleStr = serializeSchedule(schedule || []);

  // 1. Fetch all Users and UserDirectory for reference
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const userDirRange = 'UserDirectory!A:F';
  // const userDirResponse = await axios.get(
  //   `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${userDirRange}`,
  //   { headers: { Authorization: `Bearer ${token}` } }
  // );


  const userDirResponse = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/UserDirectory!A:F`,
    { headers: { Authorization: `Bearer ${token}` } }
  );



  const userDirRows = userDirResponse.data.values || [];

  // 2. Build lookup maps
  const userDirByEmail = new Map();
  const userDirByName = new Map();
  userDirRows.forEach(row => {
    const [email, orgId, userId, name, surname, role] = row;
    if (email) {
      userDirByEmail.set(email.toLowerCase().trim(), { email, orgId, userId, name, surname, role });
      const nameKey = `${name?.toLowerCase().trim()}|${surname?.toLowerCase().trim()}`;
      if (nameKey && nameKey !== '|') {
        userDirByName.set(nameKey, { email, orgId, userId, name, surname, role });
      }
    }
  });

  const userByClass = new Map();
  allUsers.forEach(u => {
    const key = `${u.email.toLowerCase().trim()}|${u.subject}|${u.grade}`;
    userByClass.set(key, u);
  });

  // 3. Process each student
  const newUsersRows = [];
  const newUserDirRows = [];
  const duplicates = [];
  const nameDuplicates = [];

  students.forEach(student => {
    const emailLower = student.email.toLowerCase().trim();
    const nameKey = `${student.name.toLowerCase().trim()}|${student.surname.toLowerCase().trim()}`;

    let existingUserDir = userDirByEmail.get(emailLower);
    if (!existingUserDir) {
      existingUserDir = userDirByName.get(nameKey);
    }

    let userId;
    let shouldAddToUserDir = false;

    if (existingUserDir) {
      userId = existingUserDir.userId;
    } else {
      userId = generateUserId(student.name, student.surname);
      shouldAddToUserDir = true;
    }

    const classKey = `${emailLower}|${subject}|${grade}`;
    const existingInClass = userByClass.get(classKey);
    if (existingInClass) {
      duplicates.push(student);
      return;
    }

    const nameInClass = allUsers.some(u =>
      u.email.toLowerCase().trim() !== emailLower &&
      u.subject === subject &&
      u.grade === grade &&
      `${u.name.toLowerCase().trim()}|${u.surname.toLowerCase().trim()}` === nameKey
    );
    if (nameInClass && !ignoreNameDuplicates) {
      nameDuplicates.push(student);
    }

    const userRow = [
      userId,
      organisationId || 'N/A',
      student.name,
      student.surname,
      student.email,
      'Student',
      grade || 'N/A',
      school || 'N/A',
      province || 'N/A',
      district || 'N/A',
      circuit || 'N/A',
      teacher || 'N/A',
      subject || 'N/A',
      googleClassroomLink || 'N/A',
      scheduleStr,
      zoomLink || 'N/A',
      zoomPassword || 'N/A',
      studentType || 'group',
      student.gender || '',
      'active',
    ];
    newUsersRows.push(userRow);

    if (shouldAddToUserDir) {
      const userDirRow = [
        student.email,
        organisationId || 'N/A',
        userId,
        student.name,
        student.surname,
        'Student'
      ];
      newUserDirRows.push(userDirRow);
      userDirByEmail.set(emailLower, { email: student.email, orgId: organisationId || 'N/A', userId, name: student.name, surname: student.surname, role: 'Student' });
      userDirByName.set(nameKey, { email: student.email, orgId: organisationId || 'N/A', userId, name: student.name, surname: student.surname, role: 'Student' });
    }
  });

  if (nameDuplicates.length > 0 && !ignoreNameDuplicates) {
    const error = new Error('Some students have the same name as existing students in this class.');
    error.duplicateNames = nameDuplicates;
    error.duplicateEmails = duplicates;
    throw error;
  }

  if (newUsersRows.length === 0 && newUserDirRows.length === 0) {
    throw new Error('No new students to add.');
  }

  const promises = [];
  if (newUsersRows.length > 0) {
    promises.push(
      axios.post(
        `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/Users!A:T:append`,
        { values: newUsersRows },
        {
          params: { valueInputOption: 'USER_ENTERED' },
          headers: { Authorization: `Bearer ${token}` }
        }
      )
    );
  }
  if (newUserDirRows.length > 0) {
    promises.push(
      axios.post(
        `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/UserDirectory!A:F:append`,
        { values: newUserDirRows },
        {
          params: { valueInputOption: 'USER_ENTERED' },
          headers: { Authorization: `Bearer ${token}` }
        }
      )
    );
  }

  await Promise.all(promises);

  return {
    added: newUsersRows.length,
    skippedEmail: duplicates.length,
    skippedName: nameDuplicates.length,
  };
};*/
export const bulkAddStudents = async (token, spreadsheetId, students, metadata, ignoreNameDuplicates = false) => {
  const { organisationId, school, province, district, circuit,
    teacher, subject, grade,
    googleClassroomLink, schedule, zoomLink, zoomPassword,
    studentType,
    termData = []
  } = metadata;

  //const scheduleStr = serializeSchedule(schedule || []);
  const scheduleStr = serializeScheduleWithTerms(schedule || [], termData);

  // 1. Fetch all existing Users and UserDirectory
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const userDirResponse = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/UserDirectory!A:F`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const userDirRows = userDirResponse.data.values || [];

  // Build lookup maps
  const userDirByEmail = new Map();
  const userDirByName = new Map();
  userDirRows.forEach(row => {
    const [email, orgId, userId, name, surname, role] = row;
    if (email) {
      userDirByEmail.set(email.toLowerCase().trim(), { email, orgId, userId, name, surname, role });
      const nameKey = `${name?.toLowerCase().trim()}|${surname?.toLowerCase().trim()}`;
      if (nameKey && nameKey !== '|') {
        userDirByName.set(nameKey, { email, orgId, userId, name, surname, role });
      }
    }
  });

  // Build a map of email -> userId from Users sheet (fallback)
  const userByEmail = new Map();
  allUsers.forEach(u => {
    if (u.email) {
      userByEmail.set(u.email.toLowerCase().trim(), u.userId);
    }
  });

  // Build existing enrollment map
  const userByClass = new Map();
  allUsers.forEach(u => {
    const key = `${u.email.toLowerCase().trim()}|${u.subject}|${u.grade}`;
    userByClass.set(key, u);
  });

  // 2. Process each student
  const newUsersRows = [];
  const newUserDirRows = [];
  const duplicates = [];
  const nameDuplicates = [];
  const errors = [];

  for (const student of students) {
    const emailLower = student.email.toLowerCase().trim();
    const nameKey = `${student.name.toLowerCase().trim()}|${student.surname.toLowerCase().trim()}`;

    // ---- Try to find existing user ----
    let existingUserDir = userDirByEmail.get(emailLower);
    let userId = null;

    if (existingUserDir) {
      // Found in UserDirectory
      userId = existingUserDir.userId;
      console.log(`✅ Found ${emailLower} in UserDirectory, userId: ${userId}`);
    } else {
      // Not in UserDirectory – check Users sheet
      const existingUserId = userByEmail.get(emailLower);
      if (existingUserId) {
        // Found in Users sheet – reuse that userId
        userId = existingUserId;
        console.log(`✅ Found ${emailLower} in Users sheet (userId: ${userId}), but missing from UserDirectory. Will add to UserDirectory.`);
        // We'll add to UserDirectory later
      } else {
        // Not found anywhere – generate new userId
        // But first, check by name to avoid duplicates (if enabled)
        const nameMatch = userDirByName.get(nameKey);
        if (nameMatch) {
          if (!ignoreNameDuplicates) {
            nameDuplicates.push(student);
            continue; // skip
          } else {
            // Ignoring name duplicates – treat as new
            userId = generateUserId(student.name, student.surname);
            console.log(`🆕 Generated new userId ${userId} for ${emailLower} (ignoring name match)`);
          }
        } else {
          userId = generateUserId(student.name, student.surname);
          console.log(`🆕 Generated new userId ${userId} for ${emailLower}`);
        }
      }
    }

    // ---- Check if already enrolled in this class ----
    const classKey = `${emailLower}|${subject}|${grade}`;
    if (userByClass.has(classKey)) {
      duplicates.push(student);
      console.log(`⏭️ ${emailLower} already enrolled in ${subject} ${grade}, skipping.`);
      continue;
    }

    // ---- Check same name in same class ----
    const nameInClass = allUsers.some(u =>
      u.email.toLowerCase().trim() !== emailLower &&
      u.subject === subject &&
      u.grade === grade &&
      `${u.name.toLowerCase().trim()}|${u.surname.toLowerCase().trim()}` === nameKey
    );
    if (nameInClass && !ignoreNameDuplicates) {
      nameDuplicates.push(student);
      continue;
    }

    // ---- Proceed to add ----
    const userRow = [
      userId,
      organisationId || 'N/A',
      student.name,
      student.surname,
      student.email,
      'Student',
      grade || 'N/A',
      school || 'N/A',
      province || 'N/A',
      district || 'N/A',
      circuit || 'N/A',
      teacher || 'N/A',
      subject || 'N/A',
      googleClassroomLink || 'N/A',
      scheduleStr,
      zoomLink || 'N/A',
      zoomPassword || 'N/A',
      studentType || 'group',
      student.gender || '',
      'active',
    ];
    newUsersRows.push(userRow);

    // Add to UserDirectory if not already present
    if (!existingUserDir && !userDirByEmail.has(emailLower)) {
      const userDirRow = [
        student.email,
        organisationId || 'N/A',
        userId,
        student.name,
        student.surname,
        'Student'
      ];
      newUserDirRows.push(userDirRow);
      // Update local maps to prevent duplicate additions in same batch
      userDirByEmail.set(emailLower, { email: student.email, orgId: organisationId || 'N/A', userId, name: student.name, surname: student.surname, role: 'Student' });
      userDirByName.set(nameKey, { email: student.email, orgId: organisationId || 'N/A', userId, name: student.name, surname: student.surname, role: 'Student' });
      console.log(`📝 Added UserDirectory row for ${emailLower} (userId: ${userId})`);
    }
  }

  // 3. Handle name duplicates
  if (nameDuplicates.length > 0 && !ignoreNameDuplicates) {
    const error = new Error('Some students have the same name as existing students in this class.');
    error.duplicateNames = nameDuplicates;
    error.duplicateEmails = duplicates;
    throw error;
  }

  if (newUsersRows.length === 0 && newUserDirRows.length === 0) {
    throw new Error('No new students to add.');
  }

  // 4. Batch append
  const promises = [];
  if (newUsersRows.length > 0) {
    promises.push(
      axios.post(
        `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/Users!A:T:append`,
        { values: newUsersRows },
        {
          params: { valueInputOption: 'USER_ENTERED' },
          headers: { Authorization: `Bearer ${token}` }
        }
      )
    );
  }
  if (newUserDirRows.length > 0) {
    promises.push(
      axios.post(
        `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/UserDirectory!A:F:append`,
        { values: newUserDirRows },
        {
          params: { valueInputOption: 'USER_ENTERED' },
          headers: { Authorization: `Bearer ${token}` }
        }
      )
    );
  }

  await Promise.all(promises);

  return {
    added: newUsersRows.length,
    skippedEmail: duplicates.length,
    skippedName: nameDuplicates.length,
  };
};

// ============================================================
//  ASSESSMENT AND MARKS FUNCTIONS
// ============================================================

// ============================================================
//  MARKS FUNCTIONS (columns: userId, organisationId, School, Province, District, Circuit, Teacher, Grade, Subject, Type, Name, Term, Student name, Student surname, Marks)
// ============================================================

/**
 * Fetch all distinct assessments for a class.
 */
export const fetchAssessmentsForClass = async (token, spreadsheetId, teacher, subject, grade) => {
  try {
    const range = 'Marks!A2:Q'; // ✅ extended to include column P (date)
    const response = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const rows = response.data.values || [];
    const assessmentSet = new Set();
    rows.forEach(row => {
      const rowTeacher = row[6];
      const rowSubject = row[8];
      const rowGrade = row[7];
      if (rowTeacher === teacher && rowSubject === subject && rowGrade === grade) {
        const type = row[9] || '';
        const name = row[10] || '';
        const term = row[11] || '';
        const date = row[15] || ''; // ✅ assessment date (column P)
        if (type && name) {
          assessmentSet.add(`${type}|${name}|${term}|${date}`);
        }
      }
    });
    return Array.from(assessmentSet).map(key => {
      const [type, name, term, date] = key.split('|');
      return { type, name, term, date };
    });
  } catch (error) {
    console.error('Error fetching assessments:', error);
    return [];
  }
};

/**
 * Fetch marks for a specific assessment.
 */
// ============================================================
//  MARKS FUNCTIONS (columns: userId, organisationId, School, Province, District, Circuit, Teacher, Grade, Subject, Type, Name, Term, Student name, Student surname, Marks, Date, LastModified)
// ============================================================

/**
 * Fetch all distinct assessments for a class.
 */

// ============================================================
//  MARKS FUNCTIONS (columns: userId, organisationId, School, Province, District, Circuit, Teacher, Grade, Subject, Type, Name, Term, Student name, Student surname, Marks, AssessmentDate, LastModified)
// ============================================================

/**
 * Fetch all distinct assessments for a class.
 * Now includes AssessmentDate.
 */
/**
 * Fetch marks for a specific assessment.
 * Returns marks with userId, studentName, studentSurname, marks, lastModified.
 */
export const fetchMarksForAssessment = async (token, spreadsheetId, teacher, subject, grade, type, name) => {
  try {
    const range = 'Marks!A2:Q'; // ✅ include up to column Q
    const response = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const rows = response.data.values || [];
    return rows
      .filter(row =>
        row[6] === teacher &&
        row[7] === grade &&
        row[8] === subject &&
        row[9] === type &&
        row[10] === name
      )
      .map(row => ({
        userId: row[0],
        studentName: row[12],
        studentSurname: row[13],
        marks: parseFloat(row[14]) || 0,
        assessmentDate: row[15] || '', // ✅ date
        lastModified: row[16] || null
      }));
  } catch (error) {
    console.error('Error fetching marks:', error);
    return [];
  }
};

/**
 * Save (upsert) a single mark record.
 * If lastModified is provided, use it; otherwise set to current timestamp.
 * We'll always update lastModified on save.
 */
export const saveMark = async (token, spreadsheetId, markData) => {
  const { userId, organisationId, school, province, district, circuit,
    teacher, grade, subject, type, name, term, assessmentDate,
    studentName, studentSurname, marks, lastModified } = markData;

  const now = lastModified || new Date().toISOString();
  const newRow = [
    userId || '',
    organisationId || '',
    school || '',
    province || '',
    district || '',
    circuit || '',
    teacher || '',
    grade || '',
    subject || '',
    type || '',
    name || '',
    term || '',
    studentName || '',
    studentSurname || '',
    marks || 0,
    assessmentDate || '',
    now
  ];

  const range = 'Marks!A2:Q';
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const rows = response.data.values || [];
  const existingIndex = rows.findIndex(row =>
    row[0] === userId &&
    row[6] === teacher &&
    row[7] === grade &&
    row[8] === subject &&
    row[9] === type &&
    row[10] === name
  );
  const rowNumber = existingIndex + 2;

  if (existingIndex !== -1) {
    const updateRange = `Marks!A${rowNumber}:Q${rowNumber}`;
    await axios.put(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${updateRange}`,
      { values: [newRow] },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  } else {
    const appendRange = 'Marks!A:Q';
    await axios.post(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${appendRange}:append`,
      { values: [newRow] },
      {
        params: { valueInputOption: 'USER_ENTERED' },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  }
};


// src/services/sheetDataService.js

/**
 * Fetch distinct classes taught by a teacher based on their students.
 * Returns an array of class objects with fields:
 *   subject, grade, googleClassroomLink, day, startTime, duration, zoomLink, zoomPassword,
 *   rowNumber (from the first student's row, used as a unique id), email (teacher email)
 */
const normalizeEmail = (email) => {
  if (!email) return '';
  const [local, domain] = email.toLowerCase().trim().split('@');
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    // Remove all dots from the local part
    return local.replace(/\./g, '') + '@gmail.com';
  }
  return local + '@' + domain;
};

/**
 * Fetch distinct classes taught by a teacher based on their students.
 * Returns an array of class objects with fields:
 *   subject, grade, googleClassroomLink, day, startTime, duration, zoomLink, zoomPassword,
 *   rowNumber, email (teacher email)
 */
export const fetchTeacherClasses = async (token, spreadsheetId, teacherEmail, teacherName) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const normalizedTeacherEmail = normalizeEmail(teacherEmail);

  // Case‑insensitive role check
  let students = allUsers.filter(u => {
    const role = (u.role || '').toLowerCase();
    return role === 'student' &&
      u.teacher &&
      normalizeEmail(u.teacher) === normalizedTeacherEmail &&
      (!u.studentType || u.studentType.toLowerCase() === 'group');
  });

  // Fallback name matching (if needed)
  if (students.length === 0 && teacherName) {
    const nameParts = teacherName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    students = allUsers.filter(u => {
      if ((u.role || '').toLowerCase() !== 'student') return false;
      const teacherField = u.teacher || '';
      return (teacherField.toLowerCase().includes(firstName.toLowerCase()) ||
        teacherField.toLowerCase().includes(lastName.toLowerCase())) &&
        (!u.studentType || u.studentType.toLowerCase() === 'group');
    });
  }

  // Group by subject+grade
  const classMap = new Map();
  students.forEach(student => {
    const key = `${student.subject}|${student.grade}`;
    if (!classMap.has(key) && student.subject && student.grade) {
      const schedule = student.schedule || [];
      let day = student.day || '';
      let startTime = student.startTime || '';
      let duration = student.duration || 0;
      if (schedule.length > 0) {
        day = schedule[0].day;
        startTime = schedule[0].startTime;
        duration = schedule[0].duration;
      }
      classMap.set(key, {
        subject: student.subject,
        grade: student.grade,
        googleClassroomLink: student.googleClassroomLink || '',
        schedule: schedule,
        termData: student.termData || [],
        day: day,
        startTime: startTime,
        duration: duration,
        zoomLink: student.zoomLink || '',
        zoomPassword: student.zoomPassword || '',
        rowNumber: student.rowNumber,
        email: student.teacher,
      });
    }
  });
  return Array.from(classMap.values());
};

// ============================================================
//  STUDENT DATA FUNCTIONS
// ============================================================
// ============================================================
//  STUDENT ATTENDANCE AND MARKS FUNCTIONS
// ============================================================

/**
 * Fetch attendance records for a specific student.
 * Requires teacher, subject, grade to identify the class.
 */
// ============================================================
//  STUDENT DATA FUNCTIONS
// ============================================================

/**
 * Fetch the class details for a student from their own row.
 * Returns a class object with all fields.
 */
export const fetchStudentClasses = async (token, spreadsheetId, userId) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  console.log('📋 All users from sheet:', allUsers);

  // Filter by userId and role (case-insensitive)
  const studentRows = allUsers.filter(u =>
    u.userId === userId &&
    u.role && u.role.toLowerCase() === 'student'
  );
  console.log('🔍 Filtered student rows:', studentRows);

  const classes = studentRows.map(row => {
    let schedule = row.schedule || [];
    if (schedule.length === 0 && (row.day || row.startTime || row.duration)) {
      schedule = [{
        day: row.day || '',
        startTime: row.startTime || '',
        duration: row.duration || 0
      }];
    }
    return {
      subject: row.subject || 'No Subject',
      grade: row.grade || '',
      teacher: row.teacher || '',
      googleClassroomLink: row.googleClassroomLink || '',
      schedule: schedule,
      zoomLink: row.zoomLink || '',
      zoomPassword: row.zoomPassword || '',
      classId: row.userId + '_' + row.subject + '_' + row.grade
    };
  });
  console.log('📚 Student classes:', classes);
  return classes;
};

/**
 * Fetch attendance records for a specific student and class.
 */
export const fetchStudentAttendance = async (token, spreadsheetId, userId, teacher, subject, grade) => {
  try {
    const range = 'Attendance!A2:P';
    const response = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const rows = response.data.values || [];

    // Trim all inputs
    const trimmedUserId = (userId || '').trim();
    const trimmedTeacher = (teacher || '').trim().toLowerCase();
    const trimmedSubject = (subject || '').trim().toLowerCase();
    const trimmedGrade = (grade || '').trim().toLowerCase();

    console.log('🔍 Fetching attendance with:', {
      trimmedUserId,
      trimmedTeacher,
      trimmedSubject,
      trimmedGrade,
      totalRows: rows.length
    });

    const filtered = rows.filter(row => {
      const rowUserId = (row[0] || '').trim();
      const rowTeacher = (row[9] || '').trim().toLowerCase();
      const rowSubject = (row[10] || '').trim().toLowerCase();
      const rowGrade = (row[11] || '').trim().toLowerCase();

      const match = rowUserId === trimmedUserId &&
        rowTeacher === trimmedTeacher &&
        rowSubject === trimmedSubject &&
        rowGrade === trimmedGrade;
      if (match) {
        console.log('✅ Match found:', row);
      }
      return match;
    });

    console.log(`📊 Attendance records found: ${filtered.length}`);
    return filtered.map(row => ({
      date: row[13],
      status: row[14] || 'Absent',
      lastModified: row[15] || null
    })).sort((a, b) => new Date(a.date) - new Date(b.date));
  } catch (error) {
    console.error('Error fetching student attendance:', error);
    return [];
  }
};

/**
 * Fetch marks for a specific student and class.
 */
export const fetchStudentMarks = async (token, spreadsheetId, userId, teacher, subject, grade) => {
  try {
    const range = 'Marks!A2:O';
    const response = await axios.get(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const rows = response.data.values || [];
    return rows
      .filter(row => {
        const rowUserId = (row[0] || '').trim();
        const rowTeacher = (row[6] || '').trim().toLowerCase();
        const rowGrade = (row[7] || '').trim().toLowerCase();
        const rowSubject = (row[8] || '').trim().toLowerCase();
        return rowUserId === userId &&
          rowTeacher === teacher.toLowerCase() &&
          rowSubject === subject.toLowerCase() &&
          rowGrade === grade.toLowerCase();
      })
      .map(row => ({
        type: row[9] || '',
        name: row[10] || '',
        term: row[11] || '',
        marks: parseFloat(row[14]) || 0
      }));
  } catch (error) {
    console.error('Error fetching student marks:', error);
    return [];
  }
};



// ============================================================
//  IMPORT STUDENTS FUNCTIONS
// ============================================================

/**
 * Get distinct (subject, grade) combinations from all students in the system.
 */
export const fetchDistinctClasses = async (token, spreadsheetId) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const classSet = new Set();
  allUsers
    .filter(u => u.role === 'Student' && u.subject && u.grade)
    .forEach(u => {
      classSet.add(`${u.subject}|${u.grade}`);
    });
  return Array.from(classSet).map(key => {
    const [subject, grade] = key.split('|');
    return { subject, grade };
  });
};

/**
 * Fetch students for a given class (by subject and grade only).
 */
export const fetchStudentsByClass = async (token, spreadsheetId, subject, grade) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  return allUsers.filter(user =>
    user.role === 'Student' &&
    user.subject === subject &&
    user.grade === grade
  );
};

// ============================================================
//  IMPORT STUDENTS FUNCTIONS
// ============================================================

/**
 * Import students from a source class into a target class.
 * @param {string} token - Access token
 * @param {string} spreadsheetId - Spreadsheet ID
 * @param {Array} students - Array of student objects from source class
 * @param {Object} targetMetadata - Metadata for the target class (teacher, subject, grade, googleClassroomLink, schedule, zoomLink, zoomPassword)
 * @param {Object} orgMetadata - Organisation details (organisationId, school, province, district, circuit)
 * @returns {Object} { added: number, skipped: number }
 */
// In sheetDataService.js
export const importStudentsToClass = async (token, spreadsheetId, students, targetMetadata, orgMetadata) => {
  const { organisationId, school, province, district, circuit } = orgMetadata;
  const { teacher, subject, grade, googleClassroomLink, schedule, zoomLink, zoomPassword } = targetMetadata;

  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const existingInTarget = new Set();
  allUsers.forEach(u => {
    if (u.teacher === teacher && u.subject === subject && u.grade === grade) {
      existingInTarget.add(u.email.toLowerCase().trim());
    }
  });

  const studentsToAdd = students.filter(s => !existingInTarget.has(s.email.toLowerCase().trim()));

  if (studentsToAdd.length === 0) {
    return { added: 0, skipped: students.length };
  }

  const scheduleStr = serializeSchedule(schedule || []);

  const usersRows = studentsToAdd.map(student => [
    student.userId,
    organisationId || '',
    student.name,
    student.surname,
    student.email,
    'Student',
    grade || '',
    school || 'N/A',
    province || 'N/A',
    district || 'N/A',
    circuit || 'N/A',
    teacher || '',
    subject || '',
    googleClassroomLink || '',
    scheduleStr,
    zoomLink || '',
    zoomPassword || '',
    'group',
    student.gender || '',
    'active',
  ]);

  await axios.post(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/Users!A:T:append`,
    { values: usersRows },
    {
      params: { valueInputOption: 'USER_ENTERED' },
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return {
    added: studentsToAdd.length,
    skipped: students.length - studentsToAdd.length
  };
};


// ============================================================
//  ADMIN USER MANAGEMENT FUNCTIONS
// ============================================================

/**
 * Admin: Add a single user (student, teacher, or admin).
 * Writes to both Users and UserDirectory sheets.
 */
export const adminAddUser = async (token, spreadsheetId, userData) => {
  const {
    name, surname, email, role, organisationId, school, province, district, circuit,
    googleClassroomLink, schedule, zoomLink, zoomPassword, grade, teacher, subject,
    studentType, gender, status,
    termData = [] // 👈 ADD THIS
  } = userData;

  // Check if user already exists in UserDirectory
  const userDirRange = 'UserDirectory!A:F';
  const userDirResponse = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/UserDirectory!A:F`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const userDirRows = userDirResponse.data.values || [];
  const existing = userDirRows.find(row => row[0].toLowerCase() === email.toLowerCase());
  if (existing) {
    throw new Error('A user with this email already exists.');
  }

  const userId = generateUserId(name, surname);

  // 👇 Use appendUser to write to Users sheet (handles termData)
  await appendUser(token, spreadsheetId, {
    userId,
    organisationId: organisationId || '',
    name,
    surname,
    email,
    role: role || 'Student',
    grade: grade || 'N/A',
    school: school || '',
    province: province || '',
    district: district || '',
    circuit: circuit || '',
    teacher: teacher || 'N/A',
    subject: subject || 'N/A',
    googleClassroomLink: googleClassroomLink || '',
    schedule: schedule || [],
    termData: termData,
    zoomLink: zoomLink || '',
    zoomPassword: zoomPassword || '',
    studentType: studentType || 'group',
    gender: gender || '',
    status: status || 'active',
  });

  // Add to UserDirectory
  const userDirRow = [
    email,
    organisationId || '',
    userId,
    name,
    surname,
    role || 'Student'
  ];
  await axios.post(
    `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/UserDirectory!A:F:append`,
    { values: [userDirRow] },
    {
      params: { valueInputOption: 'USER_ENTERED' },
      headers: { Authorization: `Bearer ${token}` }
    }
  );


  // ✅ Sync permissions after adding user
  /*try {
    // Use organisationId directly – no need for duplicate
    if (organisationId) {
      await syncOrganisationPermissions(token, organisationId);
    }
  } catch (err) {
    console.warn('Permission sync failed after adding user:', err.message);
    // Don't throw – user was already added successfully
  }*/
};

/**
 * Admin: Update an existing user.
 * Updates both Users and UserDirectory sheets.
 */
/**
 * Admin: Update an existing user.
 * Updates both Users and UserDirectory sheets.
 * Uses `!== undefined` to allow clearing fields (empty strings).
 */
export const adminUpdateUser = async (token, usersSheetId, rowNumber, userData, attendanceSheetId, marksSheetId) => {
  // 1. Fetch existing user from Users sheet
  const allUsers = await fetchAllUsers(token, usersSheetId);
  const user = allUsers.find(u => u.rowNumber === rowNumber);
  if (!user) throw new Error('User not found in Users sheet.');

  // 2. Build updated data – use userData if provided (even if empty string), else keep old
  const updatedUserData = {
    userId: user.userId,
    organisationId: userData.organisationId !== undefined ? userData.organisationId : user.organisationId,
    name: userData.name !== undefined ? userData.name : user.name,
    surname: userData.surname !== undefined ? userData.surname : user.surname,
    email: userData.email !== undefined ? userData.email : user.email,
    role: userData.role !== undefined ? userData.role : user.role,
    grade: userData.grade !== undefined ? userData.grade : user.grade,
    school: userData.school !== undefined ? userData.school : user.school,
    province: userData.province !== undefined ? userData.province : user.province,
    district: userData.district !== undefined ? userData.district : user.district,
    circuit: userData.circuit !== undefined ? userData.circuit : user.circuit,
    teacher: userData.teacher !== undefined ? userData.teacher : user.teacher,
    subject: userData.subject !== undefined ? userData.subject : user.subject,
    googleClassroomLink: userData.googleClassroomLink !== undefined ? userData.googleClassroomLink : user.googleClassroomLink,
    schedule: userData.schedule !== undefined ? userData.schedule : user.schedule,
    termData: userData.termData !== undefined ? userData.termData : (user.termData || []),
    zoomLink: userData.zoomLink !== undefined ? userData.zoomLink : user.zoomLink,
    zoomPassword: userData.zoomPassword !== undefined ? userData.zoomPassword : user.zoomPassword,
    studentType: userData.studentType !== undefined ? userData.studentType : (user.studentType || 'group'),
    gender: userData.gender !== undefined ? userData.gender : (user.gender || ''),
    status: userData.status !== undefined ? userData.status : (user.status || 'active'),
  };

  // 3. Write to Users sheet
  await updateUserByRow(token, usersSheetId, rowNumber, updatedUserData);

  // 4. Update UserDirectory (master sheet)
  const userDirectoryEntries = await fetchAllUserDirectory(token);
  const dirEntry = userDirectoryEntries.find(e => e.userId === user.userId);
  if (!dirEntry) {
    throw new Error(`UserDirectory row not found for userId: ${user.userId}`);
  }
  const targetRow = dirEntry.rowNumber;
  const userDirData = {
    email: userData.email !== undefined ? userData.email : user.email,
    organisationId: userData.organisationId !== undefined ? userData.organisationId : user.organisationId,
    userId: user.userId,
    name: userData.name !== undefined ? userData.name : user.name,
    surname: userData.surname !== undefined ? userData.surname : user.surname,
    role: userData.role !== undefined ? userData.role : user.role,
  };
  await updateUserDirectoryByRow(token, targetRow, userDirData);

  // 5. Update Attendance and Marks if the user is a student
  if ((userData.role || user.role) === 'student') {
    const userId = user.userId;
    await updateAttendanceStudentDetails(
      token,
      attendanceSheetId,
      userId,
      userData.name !== undefined ? userData.name : user.name,
      userData.surname !== undefined ? userData.surname : user.surname,
      userData.email !== undefined ? userData.email : user.email
    );
    await updateMarksStudentDetails(
      token,
      marksSheetId,
      userId,
      userData.name !== undefined ? userData.name : user.name,
      userData.surname !== undefined ? userData.surname : user.surname
    );
  }

  // ✅ Sync permissions after updating user
  /*try {
    const orgId = updatedUserData.organisationId;
    if (orgId) {
      await syncOrganisationPermissions(token, orgId);
    }
  } catch (err) {
    console.warn('Permission sync failed after updating user:', err.message);
  }*/
};



/**
 * Bulk update the status for all users in an organisation.
 * @param {string} token - OAuth access token
 * @param {string} usersSheetId - The Users sheet spreadsheet ID (from the organisation)
 * @param {string} organisationId - The organisation ID
 * @param {string} newStatus - 'active', 'partial', or 'inactive'
 * @returns {Promise<number>} - number of users updated
 */
// src/services/sheetDataService.js

export const bulkUpdateUserStatus = async (token, usersSheetId, organisationId, newStatus) => {
  if (!newStatus) throw new Error('Status is required.');

  // 1. Fetch all users from the sheet
  const allUsers = await fetchAllUsers(token, usersSheetId);
  const orgUsers = allUsers.filter(u => u.organisationId === organisationId);

  if (orgUsers.length === 0) {
    throw new Error(`No users found for organisation ${organisationId}.`);
  }

  // 2. Build batch update data
  const updateData = [];
  for (const user of orgUsers) {
    // Build the full row array (A:T) – same as updateUserByRow
    let schedule = user.schedule || [];
    if (schedule.length === 0 && (user.day || user.startTime || user.duration)) {
      schedule = [{
        day: user.day || '',
        startTime: user.startTime || '',
        duration: parseInt(user.duration, 10) || 0
      }];
    }
    const scheduleStr = serializeSchedule(schedule);
    const row = [
      user.userId || '',
      user.organisationId || 'N/A',
      user.name || '',
      user.surname || '',
      user.email || '',
      user.role || 'Student',
      user.grade || 'N/A',
      user.school || 'N/A',
      user.province || 'N/A',
      user.district || 'N/A',
      user.circuit || 'N/A',
      user.teacher || 'N/A',
      user.subject || 'N/A',
      user.googleClassroomLink || 'N/A',
      scheduleStr,
      user.zoomLink || 'N/A',
      user.zoomPassword || 'N/A',
      user.studentType || 'group',
      user.gender || '',
      newStatus, // 👈 the only changed field
    ];
    updateData.push({
      range: `Users!A${user.rowNumber}:T${user.rowNumber}`,
      values: [row],
    });
  }

  // 3. Send one batch update request
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${usersSheetId}/values:batchUpdate`;
  await axios.post(
    url,
    {
      valueInputOption: 'USER_ENTERED',
      data: updateData,
    },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  // 4. ✅ Automatically sync Drive permissions based on the new statuses
  try {
    await syncOrganisationPermissions(token, organisationId);
    console.log(`✅ Permissions synced for organisation ${organisationId}`);
  } catch (err) {
    console.warn('⚠️ Permission sync failed after bulk status update:', err.message);
    // Don't rethrow – the status update itself succeeded.
  }

  return orgUsers.length;
};




/**
 * Admin: Delete a user from both Users and UserDirectory sheets.
 */
// ============================================================
//  HELPER: Delete a row from any sheet
// ============================================================

/**
 * Delete a row from a sheet by row number.
 * @param {string} token - Access token
 * @param {string} spreadsheetId - ID of the spreadsheet
 * @param {string} sheetName - Name of the sheet (e.g., 'Users', 'UserDirectory')
 * @param {number} rowNumber - 1‑based row number to delete
 */
const deleteRowFromSheet = async (token, spreadsheetId, sheetName, rowNumber) => {
  // Get the sheet ID
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}`;
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const sheet = response.data.sheets.find(s => s.properties.title === sheetName);
  if (!sheet) throw new Error(`Sheet "${sheetName}" not found.`);

  const sheetId = sheet.properties.sheetId;

  // Delete the row (0‑based index)
  const deleteUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`;
  await axios.post(
    deleteUrl,
    {
      requests: [
        {
          deleteDimension: {
            range: {
              sheetId: sheetId,
              dimension: 'ROWS',
              startIndex: rowNumber - 1,
              endIndex: rowNumber,
            },
          },
        },
      ],
    },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
};

/**
 * Admin: Delete a user from both Users and UserDirectory sheets.
 * Physically removes the rows (no empty rows left behind).
 */
export const adminDeleteUser = async (token, spreadsheetId, user) => {
  // 1. Delete from Users sheet
  await deleteRowFromSheet(token, spreadsheetId, 'Users', user.rowNumber);

  // 2. Delete from UserDirectory (master sheet)
  const userDirectoryEntries = await fetchAllUserDirectory(token);
  const dirEntry = userDirectoryEntries.find(e => e.userId === user.userId);
  if (dirEntry) {
    await deleteRowFromSheet(token, MASTER_SHEET_ID, 'UserDirectory', dirEntry.rowNumber);
  }

  // ✅ Sync permissions after deleting user
  /*try {
    if (user.organisationId) {
      await syncOrganisationPermissions(token, user.organisationId);
    }
  } catch (err) {
    console.warn('Permission sync failed after deleting user:', err.message);
  }*/
};


// ============================================================
//  CLASS UPDATE FUNCTIONS
// ============================================================

/**
 * Update class details for all group students in a class.
 * - Only updates students with studentType === 'group'.
 * - Only updates students with empty schedule (inheriting default).
 * - Tutoring students (studentType === 'tutor') are never touched.
 */
/*export const updateClassForAllStudents = async (token, spreadsheetId, currentClass, updates, force = false) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const groupStudents = allUsers.filter(u =>
    u.role === 'Student' &&
    u.teacher === currentClass.email &&
    u.subject === currentClass.subject &&
    u.grade === currentClass.grade &&
    u.studentType === 'group'
  );

  if (groupStudents.length === 0) {
    return { updated: 0, message: 'No group students found in this class.' };
  }

  if (!force) {
    const customScheduleStudents = groupStudents.filter(s => s.schedule && s.schedule.length > 0);
    if (customScheduleStudents.length > 0) {
      const defaultStudents = groupStudents.filter(s => !s.schedule || s.schedule.length === 0);
      return {
        updated: 0,
        customCount: customScheduleStudents.length,
        defaultCount: defaultStudents.length,
        message: `${customScheduleStudents.length} student(s) have custom schedules.`
      };
    }
  }

  const scheduleStr = serializeSchedule(updates.schedule || []);

  const updatePromises = groupStudents.map(async (student) => {
    const userData = {
      userId: student.userId,
      organisationId: student.organisationId,
      name: student.name,
      surname: student.surname,
      email: student.email,
      role: student.role,
      grade: student.grade,
      school: student.school,
      province: student.province,
      district: student.district,
      circuit: student.circuit,
      teacher: student.teacher,
      subject: student.subject,
      googleClassroomLink: updates.googleClassroomLink || '',
      schedule: updates.schedule || [],
      zoomLink: updates.zoomLink || '',
      zoomPassword: updates.zoomPassword || '',
      studentType: student.studentType,
      gender: student.gender || '',
      status: student.status || 'active',
    };
    await updateUserByRow(token, spreadsheetId, student.rowNumber, userData);
  });

  await Promise.all(updatePromises);
  return { updated: groupStudents.length };
};*/


/**
 * Update class details for all group students in a class.
 * - Only updates students with studentType === 'group'.
 * - Only updates students with empty schedule (inheriting default).
 * - Tutoring students (studentType === 'tutor') are never touched.
 * - If termData is provided, it is applied to all students.
 */
export const updateClassForAllStudents = async (token, spreadsheetId, currentClass, updates, force = false) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const groupStudents = allUsers.filter(u =>
    u.role === 'Student' &&
    u.teacher === currentClass.email &&
    u.subject === currentClass.subject &&
    u.grade === currentClass.grade &&
    u.studentType === 'group'
  );

  if (groupStudents.length === 0) {
    return { updated: 0, message: 'No group students found in this class.' };
  }

  if (!force) {
    const customScheduleStudents = groupStudents.filter(s => s.schedule && s.schedule.length > 0);
    if (customScheduleStudents.length > 0) {
      const defaultStudents = groupStudents.filter(s => !s.schedule || s.schedule.length === 0);
      return {
        updated: 0,
        customCount: customScheduleStudents.length,
        defaultCount: defaultStudents.length,
        message: `${customScheduleStudents.length} student(s) have custom schedules.`
      };
    }
  }

  // ✅ Get termData from updates (if provided)
  const termData = updates.termData || [];

  const updatePromises = groupStudents.map(async (student) => {
    const userData = {
      userId: student.userId,
      organisationId: student.organisationId,
      name: student.name,
      surname: student.surname,
      email: student.email,
      role: student.role,
      grade: student.grade,
      school: student.school,
      province: student.province,
      district: student.district,
      circuit: student.circuit,
      teacher: student.teacher,
      subject: student.subject,
      googleClassroomLink: updates.googleClassroomLink || '',
      schedule: updates.schedule || [],
      termData: termData,
      zoomLink: updates.zoomLink || '',
      zoomPassword: updates.zoomPassword || '',
      studentType: student.studentType,
      gender: student.gender || '',
      status: student.status || 'active',
    };
    await updateUserByRow(token, spreadsheetId, student.rowNumber, userData);
  });

  await Promise.all(updatePromises);
  return { updated: groupStudents.length };
};


// ============================================================
//  STUDENT SCHEDULE MANAGEMENT
// ============================================================

/**
 * Update a single student's schedule.
 * @param {string} token - Access token
 * @param {string} spreadsheetId - Spreadsheet ID
 * @param {string} userId - The student's userId
 * @param {Array} schedule - Array of sessions { day, startTime, duration }
 * @param {Array} termData - Array of terms { term, termStart, termEnd }
 */
/*export const updateStudentSchedule = async (token, spreadsheetId, userId, schedule, termData = []) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const student = allUsers.find(u => u.userId === userId);
  if (!student) throw new Error('Student not found.');

  const userData = {
    userId: student.userId,
    organisationId: student.organisationId,
    name: student.name,
    surname: student.surname,
    email: student.email,
    role: student.role,
    grade: student.grade,
    school: student.school,
    province: student.province,
    district: student.district,
    circuit: student.circuit,
    teacher: student.teacher,
    subject: student.subject,
    googleClassroomLink: student.googleClassroomLink,
    schedule: schedule || [],
    termData: termData || [], 
    zoomLink: student.zoomLink,
    zoomPassword: student.zoomPassword,
    studentType: student.studentType || 'tutor',
    gender: student.gender || '',
    status: student.status || 'active',
  };
  await updateUserByRow(token, spreadsheetId, student.rowNumber, userData);
};*/

/**
 * Update a single student's schedule by row number.
 * @param {string} token - Access token
 * @param {string} spreadsheetId - Spreadsheet ID
 * @param {number} rowNumber - The row number of the student in Users sheet
 * @param {Array} schedule - Array of sessions { day, startTime, duration }
 * @param {Array} termData - Array of terms { term, termStart, termEnd }
 */
export const updateStudentSchedule = async (token, spreadsheetId, rowNumber, schedule, termData = []) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const student = allUsers.find(u => u.rowNumber === rowNumber);
  if (!student) throw new Error(`Student not found at row ${rowNumber}`);

  const userData = {
    userId: student.userId,
    organisationId: student.organisationId,
    name: student.name,
    surname: student.surname,
    email: student.email,
    role: student.role,
    grade: student.grade,
    school: student.school,
    province: student.province,
    district: student.district,
    circuit: student.circuit,
    teacher: student.teacher,
    subject: student.subject,
    googleClassroomLink: student.googleClassroomLink,
    schedule: schedule || [],
    termData: termData || [],
    zoomLink: student.zoomLink,
    zoomPassword: student.zoomPassword,
    studentType: student.studentType || 'tutor',
    gender: student.gender || '',
    status: student.status || 'active',
  };
  await updateUserByRow(token, spreadsheetId, rowNumber, userData);
};




/**
 * Update a tutoring student's details across all rows with the same userId.
 */

export const updateTutoringStudent = async (
  token,
  usersSheetId,
  userId,
  studentData,
  attendanceSheetId,
  marksSheetId
) => {
  // Fallback: if attendance or marks IDs are not provided, use usersSheetId
  const attSheetId = attendanceSheetId || usersSheetId;
  const markSheetId = marksSheetId || usersSheetId;

  // Fetch all users from the Users sheet
  const allUsers = await fetchAllUsers(token, usersSheetId);
  const userRows = allUsers.filter(u => u.userId === userId && u.role === 'Student');
  if (userRows.length === 0) {
    throw new Error('Student not found.');
  }

  // 1. Update all Users rows – only global fields, preserve subject-specific ones
  const updatePromises = userRows.map(async (user) => {
    const updatedUserData = {
      userId: user.userId,
      organisationId: user.organisationId,
      name: studentData.name !== undefined ? studentData.name : user.name,
      surname: studentData.surname !== undefined ? studentData.surname : user.surname,
      email: studentData.email !== undefined ? studentData.email : user.email,
      role: user.role,
      grade: user.grade, 
      school: user.school,
      province: user.province,
      district: user.district,
      circuit: user.circuit,
      teacher: user.teacher,
      subject: user.subject, 
      googleClassroomLink: user.googleClassroomLink, 
      schedule: user.schedule, 
      termData: user.termData || [], 
      zoomLink: user.zoomLink, // keep existing
      zoomPassword: user.zoomPassword, 
      studentType: user.studentType || 'tutor',
      gender: studentData.gender !== undefined ? studentData.gender : user.gender,
      status: studentData.status !== undefined ? studentData.status : user.status,
    };
    await updateUserByRow(token, usersSheetId, user.rowNumber, updatedUserData);
  });
  await Promise.all(updatePromises);

  // 2. Update UserDirectory (master sheet) – only global fields
  const userDirectoryEntries = await fetchAllUserDirectory(token);
  const dirEntry = userDirectoryEntries.find(e => e.userId === userId);
  if (dirEntry) {
    const userDirData = {
      email: studentData.email !== undefined ? studentData.email : userRows[0].email,
      organisationId: userRows[0].organisationId || '',
      userId,
      name: studentData.name !== undefined ? studentData.name : userRows[0].name,
      surname: studentData.surname !== undefined ? studentData.surname : userRows[0].surname,
      role: 'Student',
    };
    await updateUserDirectoryByRow(token, dirEntry.rowNumber, userDirData);
  } else {
    console.warn(`UserDirectory row not found for userId: ${userId}. Skipping directory update.`);
  }

  // 3. Update Attendance rows – update name, surname, email
  await updateAttendanceStudentDetails(
    token,
    attSheetId,
    userId,
    studentData.name !== undefined ? studentData.name : userRows[0].name,
    studentData.surname !== undefined ? studentData.surname : userRows[0].surname,
    studentData.email !== undefined ? studentData.email : userRows[0].email
  );

  // 4. Update Marks rows – update name, surname
  await updateMarksStudentDetails(
    token,
    markSheetId,
    userId,
    studentData.name !== undefined ? studentData.name : userRows[0].name,
    studentData.surname !== undefined ? studentData.surname : userRows[0].surname
  );

  return { updated: userRows.length };
};


// src/services/sheetDataService.js

/**
 * Add a new subject for an existing tutoring student.
 * Creates a new row in the Users sheet for the same userId.
 */
export const addTutorSubject = async (token, spreadsheetId, userId, subjectData) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const existingStudent = allUsers.find(u => u.userId === userId && u.role === 'Student');
  if (!existingStudent) throw new Error('Student not found.');

  const userData = {
    userId: existingStudent.userId,
    organisationId: existingStudent.organisationId,
    name: existingStudent.name,
    surname: existingStudent.surname,
    email: existingStudent.email,
    role: existingStudent.role,
    grade: subjectData.grade || existingStudent.grade,
    school: existingStudent.school,
    province: existingStudent.province,
    district: existingStudent.district,
    circuit: existingStudent.circuit,
    teacher: existingStudent.teacher,
    subject: subjectData.subject,
    googleClassroomLink: subjectData.googleClassroomLink || '',
    schedule: subjectData.schedule || [],
    termData: subjectData.termData || [], 
    zoomLink: subjectData.zoomLink || '',
    zoomPassword: subjectData.zoomPassword || '',
    studentType: existingStudent.studentType || 'tutor',
    gender: existingStudent.gender || '',
  };
  await appendUser(token, spreadsheetId, userData);
};


/**
 * Update name/surname/email in all Attendance rows for a student.
 */
export const updateAttendanceStudentDetails = async (token, spreadsheetId, userId, name, surname, email) => {
  const range = 'Attendance!A2:R';
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const rows = response.data.values || [];
  const updatePromises = [];
  rows.forEach((row, index) => {
    if (row[0] === userId) {
      const rowNum = index + 2;
      const updateRange = `Attendance!A${rowNum}:R${rowNum}`;
      const newRow = [...row];
      newRow[2] = name;   // Name
      newRow[3] = surname; // Surname
      newRow[4] = email;   // Email
      updatePromises.push(
        axios.put(
          `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${updateRange}`,
          { values: [newRow] },
          {
            params: { valueInputOption: 'USER_ENTERED' },
            headers: { Authorization: `Bearer ${token}` }
          }
        )
      );
    }
  });
  await Promise.all(updatePromises);
};

/**
 * Update student name/surname in all Marks rows for a student.
 */
export const updateMarksStudentDetails = async (token, spreadsheetId, userId, name, surname) => {
  const range = 'Marks!A2:Q';
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const rows = response.data.values || [];
  const updatePromises = [];
  rows.forEach((row, index) => {
    if (row[0] === userId) {
      const rowNum = index + 2;
      const updateRange = `Marks!A${rowNum}:Q${rowNum}`;
      const newRow = [...row];
      newRow[12] = name;   // Student name
      newRow[13] = surname; // Student surname
      updatePromises.push(
        axios.put(
          `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${updateRange}`,
          { values: [newRow] },
          {
            params: { valueInputOption: 'USER_ENTERED' },
            headers: { Authorization: `Bearer ${token}` }
          }
        )
      );
    }
  });
  await Promise.all(updatePromises);
};


// ============================================================
//  TOPICS FUNCTIONS
// ============================================================

/**
 * Helper: Fallback term detection from date (used only if teacher leaves term blank).
 */
const detectTerm = (dateStr) => {
  const date = new Date(dateStr);
  const month = date.getMonth(); // 0=Jan
  if (month >= 0 && month <= 2) return 'Term 1';
  if (month >= 3 && month <= 5) return 'Term 2';
  if (month >= 6 && month <= 8) return 'Term 3';
  if (month >= 9 && month <= 11) return 'Term 4';
  return 'N/A';
};

/**
 * Fetch all topics for a specific class.
 * Sheet columns: A=CreatedDate, B=Teacher, C=Subject, D=Grade,
 * E=Topic, F=Subtopics, G=Term, H=TermStart, I=TermEnd, J=LastModified
 */
export const fetchTopicsForClass = async (token, spreadsheetId, teacher, subject, grade) => {
  const range = 'Topics!A2:J';
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const rows = response.data.values || [];

  const result = [];
  rows.forEach((row, index) => {
    const rowTeacher = row[1] || '';
    const rowSubject = row[2] || '';
    const rowGrade = row[3] || '';

    // ✅ Only match teacher and subject. Grade is optional.
    // If grade is provided (non-empty), also match the grade.
    const gradeMatches = !grade || rowGrade === grade;

    if (rowTeacher === teacher && rowSubject === subject && gradeMatches) {
      result.push({
        createdDate: row[0] || '',
        teacher: rowTeacher,
        subject: rowSubject,
        grade: rowGrade,
        topic: row[4] || '',
        subtopics: row[5] || '',
        term: row[6] || '',
        termStart: row[7] || '',
        termEnd: row[8] || '',
        lastModified: row[9] || null,
        rowNumber: index + 2,
      });
    }
  });

  return result.sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
};



/**
 * Add a new topic.
 * Throws an error if a topic with the same teacher, subject, grade, topic, subtopic, and term already exists.
 * Uses trimming and case‑insensitive comparison.
 */
export const addTopic = async (token, spreadsheetId, topicData) => {
  const { teacher, subject, grade, topic, subtopics, term, termStart, termEnd } = topicData;

  // Normalize inputs: trim and lowercase for comparison
  const normalizedTopic = (topic || '').trim();
  const normalizedSubtopics = (subtopics || '').trim();
  const normalizedTerm = (term || '').trim();

  // 1. Fetch existing topics for this exact class (teacher + subject + grade)
  const existingTopics = await fetchTopicsForClass(token, spreadsheetId, teacher, subject, grade);

  // 2. Check for duplicate based on topic, subtopic, AND term (normalized)
  const duplicate = existingTopics.some(
    t => t.topic.trim().toLowerCase() === normalizedTopic.toLowerCase() &&
      t.subtopics.trim().toLowerCase() === normalizedSubtopics.toLowerCase() &&
      t.term.trim().toLowerCase() === normalizedTerm.toLowerCase()
  );

  if (duplicate) {
    throw new Error(
      `Topic "${normalizedTopic}" with subtopic "${normalizedSubtopics || 'None'}" already exists for Term "${normalizedTerm || 'N/A'}" in this class.`
    );
  }

  // 3. Proceed with insertion (store trimmed values)
  const now = new Date().toISOString();
  const createdDate = new Date().toISOString().slice(0, 10);
  const finalTerm = normalizedTerm || detectTerm(createdDate);
  const row = [
    createdDate,
    teacher,
    subject,
    grade,
    normalizedTopic,
    normalizedSubtopics,
    finalTerm,
    (termStart || '').trim(),
    (termEnd || '').trim(),
    now,
  ];
  const range = 'Topics!A:J';
  await axios.post(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}:append`,
    { values: [row] },
    {
      params: { valueInputOption: 'USER_ENTERED' },
      headers: { Authorization: `Bearer ${token}` }
    }
  );
};


/**
 * Update an existing topic.
 * Checks for duplicate (topic + subtopic + term) excluding the current row, using trimming.
 */
export const updateTopic = async (token, spreadsheetId, rowNumber, topicData) => {
  const { topic, subtopics, term, termStart, termEnd } = topicData;

  // Normalize inputs
  const normalizedTopic = (topic || '').trim();
  const normalizedSubtopics = (subtopics || '').trim();
  const normalizedTerm = (term || '').trim();

  // 1. Fetch the existing row to obtain teacher, subject, grade
  const fetchRange = `Topics!A${rowNumber}:J${rowNumber}`;
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${fetchRange}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const existing = response.data.values[0];
  if (!existing || existing.length === 0) {
    throw new Error('Topic not found.');
  }

  const [createdDate, teacher, subject, grade] = existing;

  // 2. Fetch all topics for this class (teacher + subject + grade)
  const allTopics = await fetchTopicsForClass(token, spreadsheetId, teacher, subject, grade);

  // 3. Check for duplicate (topic + subtopic + term), excluding the current row, using trimmed comparison
  const duplicate = allTopics.some(
    t => t.topic.trim().toLowerCase() === normalizedTopic.toLowerCase() &&
      t.subtopics.trim().toLowerCase() === normalizedSubtopics.toLowerCase() &&
      t.term.trim().toLowerCase() === normalizedTerm.toLowerCase() &&
      t.rowNumber !== rowNumber
  );

  if (duplicate) {
    throw new Error(
      `Another entry with topic "${normalizedTopic}", subtopic "${normalizedSubtopics || 'None'}", and term "${normalizedTerm || 'N/A'}" already exists for this class.`
    );
  }

  // 4. Prepare updated row with trimmed values
  const now = new Date().toISOString();
  const finalTerm = normalizedTerm || existing[6] || detectTerm(existing[0]);
  const newRow = [
    existing[0], // createdDate (unchanged)
    existing[1], // teacher (unchanged)
    existing[2], // subject (unchanged)
    existing[3], // grade (unchanged)
    normalizedTopic || existing[4],
    normalizedSubtopics || existing[5],
    finalTerm,
    (termStart || existing[7] || '').trim(),
    (termEnd || existing[8] || '').trim(),
    now,
  ];

  // 5. Update the row
  const updateRange = `Topics!A${rowNumber}:J${rowNumber}`;
  await axios.put(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${updateRange}`,
    { values: [newRow] },
    {
      params: { valueInputOption: 'USER_ENTERED' },
      headers: { Authorization: `Bearer ${token}` }
    }
  );
};



// ============================================================
//  TOPICS IMPORT HELPERS
// ============================================================

/**
 * Fetch ALL topics from the Topics sheet (no filter).
 * Used only for the import modal to get teacher lists.
 */
const fetchAllTopicsFromSheet = async (token, spreadsheetId) => {
  const range = 'Topics!A2:J';
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const rows = response.data.values || [];
  return rows.map((row, index) => ({
    createdDate: row[0] || '',
    teacher: row[1] || '',
    subject: row[2] || '',
    grade: row[3] || '',
    topic: row[4] || '',
    subtopics: row[5] || '',
    term: row[6] || '',
    termStart: row[7] || '',
    termEnd: row[8] || '',
    lastModified: row[9] || null,
    rowNumber: index + 2,
  }));
};

/**
 * Get distinct teachers who have topics for a given subject and grade.
 * Excludes the current teacher's email.
 */
export const fetchTeachersForSubjectGrade = async (token, spreadsheetId, subject, grade, currentTeacher) => {
  const allTopics = await fetchAllTopicsFromSheet(token, spreadsheetId);
  const teachers = new Set();
  allTopics.forEach(t => {
    if (t.subject === subject && t.grade === grade && t.teacher && t.teacher !== currentTeacher) {
      teachers.add(t.teacher);
    }
  });
  return Array.from(teachers).sort();
};

/**
 * Get topics for a specific teacher, subject, and grade (already exists as fetchTopicsForClass).
 * We'll reuse fetchTopicsForClass.
 */


/**
 * Delete a topic (clear row).
 */

const getSheetIdByName = async (token, spreadsheetId, sheetName) => {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}`;
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const sheet = response.data.sheets.find(s => s.properties.title === sheetName);
  if (!sheet) throw new Error(`Sheet "${sheetName}" not found.`);
  return sheet.properties.sheetId;
};

/**
 * Delete a topic row (physically removes the row).
 */
export const deleteTopic = async (token, spreadsheetId, rowNumber) => {
  const sheetId = await getSheetIdByName(token, spreadsheetId, 'Topics');
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`;
  const requestBody = {
    requests: [
      {
        deleteDimension: {
          range: {
            sheetId: sheetId,
            dimension: 'ROWS',
            startIndex: rowNumber - 1, // 0‑based index
            endIndex: rowNumber,       // exclusive
          },
        },
      },
    ],
  };
  await axios.post(url, requestBody, {
    headers: { Authorization: `Bearer ${token}` }
  });
};


// ============================================================
//  TOPICS HELPERS FOR ATTENDANCE
// ============================================================

/**
 * Get unique terms with start/end dates for a class.
 */
export const fetchTermsForClass = async (token, spreadsheetId, teacher, subject, grade) => {
  const allTopics = await fetchTopicsForClass(token, spreadsheetId, teacher, subject, grade);
  const termMap = new Map();
  allTopics.forEach(t => {
    if (t.term) {
      if (!termMap.has(t.term)) {
        termMap.set(t.term, {
          term: t.term,
          termStart: t.termStart,
          termEnd: t.termEnd,
        });
      } else {
        // Merge dates (earliest start, latest end)
        const existing = termMap.get(t.term);
        if (t.termStart && (!existing.termStart || t.termStart < existing.termStart)) {
          existing.termStart = t.termStart;
        }
        if (t.termEnd && (!existing.termEnd || t.termEnd > existing.termEnd)) {
          existing.termEnd = t.termEnd;
        }
      }
    }
  });
  return Array.from(termMap.values()).sort((a, b) => a.term.localeCompare(b.term));
};

/**
 * Get all topics as options for dropdown.
 */
export const fetchTopicOptions = async (token, spreadsheetId, teacher, subject, grade) => {
  const allTopics = await fetchTopicsForClass(token, spreadsheetId, teacher, subject, grade);
  // Remove duplicates (if same topic+subtopics appear multiple times)
  const unique = new Map();
  allTopics.forEach(t => {
    const key = `${t.topic}|${t.subtopics}`;
    if (!unique.has(key)) {
      unique.set(key, {
        value: t.topic + (t.subtopics ? ` (${t.subtopics})` : ''),
        topic: t.topic,
        subtopics: t.subtopics,
      });
    }
  });
  return Array.from(unique.values());
};


/**
 * Batch add multiple topics in one request.
 * No duplicate check – you can add a flag to skip duplicates if needed.
 */
export const addTopicsBatch = async (token, spreadsheetId, topicsData) => {
  if (!topicsData || topicsData.length === 0) return { added: 0, skipped: 0 };

  // Fetch existing topics for this class (teacher + subject + grade)
  const teacher = topicsData[0].teacher;
  const subject = topicsData[0].subject;
  const grade = topicsData[0].grade;
  const existing = await fetchTopicsForClass(token, spreadsheetId, teacher, subject, grade);

  // Build a set of existing keys (topic, subtopic, term) – case‑insensitive, trimmed
  const existingKeys = new Set();
  existing.forEach(t => {
    const key = `${t.topic.trim().toLowerCase()}|${t.subtopics.trim().toLowerCase()}|${t.term.trim().toLowerCase()}`;
    existingKeys.add(key);
  });

  // Filter out duplicates
  const rowsToAdd = [];
  const skipped = [];
  topicsData.forEach(td => {
    const key = `${td.topic.trim().toLowerCase()}|${td.subtopics.trim().toLowerCase()}|${td.term.trim().toLowerCase()}`;
    if (existingKeys.has(key)) {
      skipped.push(`${td.topic} | ${td.subtopics}`);
    } else {
      rowsToAdd.push(td);
    }
  });

  if (rowsToAdd.length === 0) {
    return { added: 0, skipped: skipped.length };
  }

  // Build rows for the batch append
  const now = new Date().toISOString();
  const createdDate = new Date().toISOString().slice(0, 10);
  const rows = rowsToAdd.map(td => {
    const term = (td.term || '').trim() || detectTerm(createdDate);
    return [
      createdDate,
      td.teacher,
      td.subject,
      td.grade,
      (td.topic || '').trim(),
      (td.subtopics || '').trim(),
      term,
      (td.termStart || '').trim(),
      (td.termEnd || '').trim(),
      now,
    ];
  });

  const range = 'Topics!A:J';
  await axios.post(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}:append`,
    { values: rows },
    {
      params: { valueInputOption: 'USER_ENTERED' },
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return { added: rowsToAdd.length, skipped: skipped.length, skippedDetails: skipped };
};

/**
 * Update School, Province, District, Circuit for all users in an organisation.
 */
export const updateOrganisationDefaults = async (token, spreadsheetId, organisationId, defaults) => {
  const allUsers = await fetchAllUsers(token, spreadsheetId);
  const usersToUpdate = allUsers.filter(u => u.organisationId === organisationId);

  if (usersToUpdate.length === 0) {
    console.warn('No users found for organisation:', organisationId);
    return;
  }

  const updatePromises = usersToUpdate.map(async (user) => {
    const updatedUserData = {
      ...user,
      school: defaults.school || user.school,
      province: defaults.province || user.province,
      district: defaults.district || user.district,
      circuit: defaults.circuit || user.circuit,
    };
    await updateUserByRow(token, spreadsheetId, user.rowNumber, updatedUserData);
  });

  await Promise.all(updatePromises);
  console.log(`Updated organisation defaults for ${usersToUpdate.length} users.`);
};

// ============================================================
//  PERMISSION SYNC FUNCTIONS
// ============================================================

/**
 * Get current permissions for a file (excluding owner)
 */
const getFilePermissions = async (token, fileId) => {
  try {
    const response = await axios.get(
      `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { fields: 'permissions(id,emailAddress,type,role)' }
      }
    );
    const perms = response.data.permissions || [];
    // Filter out the owner (usually the creator) to avoid removing them
    return perms.filter(p => p.type === 'user' && p.role !== 'owner');
  } catch (err) {
    console.error(`Failed to get permissions for ${fileId}:`, err.message);
    return [];
  }
};

/**
 * Add a permission to a file for a single user
 */
/*const addFilePermission = async (token, fileId, email, role) => {
  try {
    await axios.post(
      `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
      {
        type: 'user',
        role: role,
        emailAddress: email,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    console.log(`✅ Added ${role} permission for ${email} to ${fileId}`);
    return true;
  } catch (err) {
    console.error(`❌ Failed to add permission for ${email} to ${fileId}:`, err.message);
    throw err;
  }
};*/

const addFilePermission = async (token, fileId, email, role) => {
  try {
    await axios.post(
      `https://www.googleapis.com/drive/v3/files/${fileId}/permissions`,
      {
        type: 'user',
        role: role,
        emailAddress: email,
      },
      {
        params: {
          sendNotificationEmail: false, // 👈 Suppress email notification
        },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    console.log(`✅ Added ${role} permission for ${email} to ${fileId}`);
    return true;
  } catch (err) {
    console.error(`❌ Failed to add permission for ${email} to ${fileId}:`, err.message);
    throw err;
  }
};


/**
 * Remove a permission from a file
 */
const removeFilePermission = async (token, fileId, permissionId) => {
  try {
    await axios.delete(
      `https://www.googleapis.com/drive/v3/files/${fileId}/permissions/${permissionId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    console.log(`✅ Removed permission ${permissionId} from ${fileId}`);
    return true;
  } catch (err) {
    console.error(`❌ Failed to remove permission ${permissionId} from ${fileId}:`, err.message);
    throw err;
  }
};

/**
 * Determine the permission role based on user status.
 * @param {string} status - 'active', 'partial', 'inactive'
 * @returns {string|null} - 'writer', 'reader', or null (no permission)
 */
const getPermissionRole = (status) => {
  if (!status) return null;
  const s = status.toLowerCase().trim();
  if (s === 'active' || s === 'active ') return 'writer';
  if (s === 'partial' || s === 'partial active') return 'reader';
  return null; // inactive or unknown → no permission
};



/**
 * Get all spreadsheet IDs for an organisation from the master sheet.
 */
export const getOrganisationSpreadsheetIds = async (token, organisationId) => {
  const orgs = await fetchAllOrganisations(token);
  const org = orgs.find(o => o.organisationId === organisationId);
  if (!org) throw new Error(`Organisation ${organisationId} not found`);
  return [
    org.usersSpreadsheetId || org.spreadsheetId,
    org.attendanceSpreadsheetId || org.spreadsheetId,
    org.marksSpreadsheetId || org.spreadsheetId,
    org.topicsSpreadsheetId || org.spreadsheetId,
  ].filter(Boolean);
};

/**
 * Sync permissions for a single file against the target map.
 */

const syncFilePermissions = async (token, fileId, targetMap, revokeMissing) => {
  // 1. Get current permissions (excluding the owner)
  const currentPerms = await getFilePermissions(token, fileId);
  const currentMap = new Map();
  currentPerms.forEach(p => {
    if (p.emailAddress) {
      currentMap.set(p.emailAddress.toLowerCase().trim(), { id: p.id, role: p.role });
    }
  });

  const operations = [];

  // 2. Process each target (email -> desired role)
  for (const [email, targetRole] of targetMap.entries()) {
    if (currentMap.has(email)) {
      const { id, role: currentRole } = currentMap.get(email);
      if (currentRole !== targetRole) {
        // Role changed – remove old permission, add new one
        operations.push(
          removeFilePermission(token, fileId, id)
            .then(() => addFilePermission(token, fileId, email, targetRole))
        );
      }
      // Remove from currentMap so it won't be treated as an extra
      currentMap.delete(email);
    } else {
      // New email – add permission
      operations.push(addFilePermission(token, fileId, email, targetRole));
    }
  }

  // 3. Remove extra permissions (if revokeMissing is true)
  if (revokeMissing) {
    for (const [email, { id }] of currentMap.entries()) {
      operations.push(removeFilePermission(token, fileId, id));
    }
  }

  // 4. Execute all operations in parallel
  if (operations.length) {
    const results = await Promise.allSettled(operations);
    const added = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    console.log(`📊 ${fileId}: ${operations.length} operations (${added} succeeded, ${failed} failed)`);
    if (failed > 0) {
      // Log errors but don't throw – you may adjust as needed
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.error(`Operation ${i} failed:`, r.reason);
        }
      });
    }
  }
};

/**
 * Sync spreadsheet permissions for all users in an organisation.
 * @param {string} token - OAuth access token
 * @param {string} organisationId - ID of the organisation
 * @param {Array<string>} spreadsheetIds - list of spreadsheet IDs to sync (optional, will fetch if not provided)
 * @param {boolean} revokeMissing - if true, remove permissions for users not in the list (default: true)
 */
export const syncOrganisationPermissions = async (token, organisationId, spreadsheetIds = null, revokeMissing = true) => {
  if (!organisationId) {
    console.warn('No organisationId provided for sync.');
    return;
  }

  // 1. Get spreadsheet IDs if not provided
  let ids = spreadsheetIds;
  if (!ids || ids.length === 0) {
    ids = await getOrganisationSpreadsheetIds(token, organisationId);
  }
  if (ids.length === 0) {
    console.warn('No spreadsheet IDs found for organisation:', organisationId);
    return;
  }

  // 2. Fetch all users for this organisation (from the Users sheet)
  const usersSheetId = ids[0];
  const allUsers = await fetchAllUsers(token, usersSheetId);
  const orgUsers = allUsers.filter(u => u.organisationId === organisationId);

  // 3. Build target email -> permission role mapping
  const targetMap = new Map();
  for (const user of orgUsers) {
    const role = getPermissionRole(user.status);
    if (role) {
      const email = user.email.trim().toLowerCase();
      targetMap.set(email, role);
    }
  }

  console.log(`📊 Syncing permissions for ${orgUsers.length} users (${targetMap.size} with permissions)`);

  // 4. For each spreadsheet, sync permissions
  for (const fileId of ids) {
    if (!fileId) continue;
    await syncFilePermissions(token, fileId, targetMap, revokeMissing);
  }
};



